import { appstr } from "../utils/appstr";
import { BreakContent } from "../utils/BreakContent";
import { CalcBorderWidth } from "../utils/CalcBorderWidth";
import { ColWidthSum } from "../utils/ColWidthSum";
import { ConvertNull } from "../utils/ConvertNull";
import { Div2Floor } from "../utils/Div2Floor";
import {
  enumBoxHeightMode,
  enumBoxShapes,
  enumBoxSubTypes,
  enumBoxType,
  enumBoxWidthMode,
  enumBranchStyle,
  enumCalcCode,
  enumCellContentType,
  enumCellObjectSubtypes,
  enumCellRefType,
  enumContainerType,
  enumDashStyle,
  enumElementBorderStyle,
  enumElementLayer,
  enumFieldDisplayMode,
  enumFieldType,
  enumFillMode,
  enumFlagType,
  enumHierarchyTypes,
  enumHorzCentering,
  enumHorzPositioning,
  enumPictureType,
  kPhotoID,
  enumEmptyPositionLayoutType,
  enumSubChartFlag,
  enumUpdateProperty,
  enumVertCentering,
  enumVertPositioning,
  enumWrapTextMode,
  kDefaultBoxPadding,
  kDefaultCellHeight,
  kDenseMargins,
  kHorzBoxSpacing,
  kManualMargins,
  kMaxBoxHeight,
  kMinBoxHeight,
  kMinLastColWidth,
  kPickerColorCodes,
  kSCArrowOffset,
  kStandardMargins,
  kTriAnnoWCalc,
  kVertBoxSpacing,
  enumImageIndex,
  kExtraSmallIcon
} from "../constants";
import { GetSCArrowHeight } from "../utils/GetSCArrowHeight";
import { IsDefaultCP } from "../utils/IsDefaultCP";
import { IsPureVerticalStyle } from "../utils/IsPureVerticalStyle";
import { MeasureText2 } from "../utils/MeasureText2";
import { QBoxCellDesc } from "./QBoxCellDesc";
import { QConnectingLineFormat } from "./QConnectingLineFormat";
import { QPoint } from "./QPoint";
import { QRect } from "./QRect";
import { QXMLObject } from "./QXMLObject";
import { Globals } from "./Globals";
import { MeasureTextHeight } from "../utils/MeasureTextHeight";
import { IsAssistantStyle } from "../utils/IsAssistantStyle";
import { MarginModeValid } from "../utils/MarginModeValid";
import { QColorPickerResult } from "./QColorPickerResult";
import { SetupTableCell } from "../utils/SetupTableCell";
import { StringToBool } from "../utils/StringToBool";

export class QBoxLayout extends QXMLObject {
  constructor() {
    super();
    this.fBoxWidth = 0; // Width of the box
    this.fNumCols = 1; // Number of columns
    this.fNumRows = 1; // Number of rows

    this.fBoxBorderColor = "#000000";
    this.fShowShadow = false;

    this.fBoxShapeType = enumBoxShapes.rectangle;

    this.fBoxFillMode = enumFillMode.solidFill;
    this.fBoxFillColor = "#FFFFFF";
    this.fBoxFillColorGradient = "#FFFFFF";

    this.fConnectingLineDesc = null;

    this.fBorderThickness = 1;
    this.fBorderStyle = enumElementBorderStyle.singleBorder;
    this.fDashStyle = enumDashStyle.nodash;

    this.fColWidths = null; // Width of the columns - minimum is 3 (pad, cell, pad)
    //	this.fRowHeights = null;			// Height of rows

    this.fBranchStyle = enumBranchStyle.tree;
    this.fBranchGroup = 0;

    this.fBoxPadding = kDefaultBoxPadding;
    this.fCellList = null;
    this.fPositionX = null;
    this.fPositionY = null;

    this.fWidthSizingMode = enumBoxWidthMode.fixedwidth;
    this.fWidthMin = -1;
    this.fWidthMax = -1;

    this.fHeightSizingMode = enumBoxHeightMode.autosmart;
    this.fFixedHeight = null;
    this.fSmartHeight = null;

    this.fContentAlign = "Top";

    this.fCenterChartHorz = enumHorzCentering.userplacement;
    this.fCenterChartVert = enumVertCentering.userplacement;

    this.fHorzPositioning = enumHorzPositioning.stayLeft;
    this.fVertPositioning = enumVertPositioning.stayTop;
    this.fDistRight = 0;
    this.fDistBottom = 0;

    this.fMarginMode = null;
    this.fBMargins = null;
    this.fMgrVSpacing1 = null;
    this.fMgrVSpacing2 = null;

    this.fBSpacing = null;

    this.fObjID = null;
    this.fDBObjRef = null;
    this.fSubChartID = null;
    this.fSubChartFlag = enumSubChartFlag.nobreak;
    this.fConnectorDashStyle = null; // enumDashStyle.nodash;
    this.fConnectorColor = null;

    this.fObjClassName = null;

    // Calculated as part of auto layout
    this.fColumnIndex = 0;
    //this.fRowIndex = 0;
    this.fRowPairMinHeight = null;

    this.fColumnPositions = null;

    this.fIsBoxStyle = false;
    this.fBoxStyleName = null;
    this.fBoxStyleID = null;
    this.fSet = null; // Box style set (e.g., "ADP", "isolved")

    this.fBoxType = enumBoxType.standard;
    this.fBoxSubType = enumBoxSubTypes.chartBox;

    this.fLayer = enumElementLayer.content;
    this.fDocRef = null; // Used for AutoText

    this.fAutoSmartVFlag = false;
    this.fAutoSmartHFlag = false;

    this.fUseCalcWidth = true;

    this.fLayoutLibID = null;
    this.fLayoutLibRefID = null;

    this.fExpandRow = null; // Temp flag

    this.fUserPositionX = null;
    this.fUserPositionY = null;

    this.fSCNodeRef = null;

    this.fLockFlag = 0;
    this.fCollapsed = false;
    this.fHidden = false;

    this.fFlagType = enumFlagType.none;
    this.fFlagColor1 = null;
    this.fFlagColor2 = null;
    this.fFlagColor3 = null;
    this.fFlagLabel = null;
    this.fHighlightInfo = null;
    this.fTriPosition = 0;

    // MRB Support
    this.fContainerType = enumContainerType.nonmrb;
    this.fContainerNumCols = 1;
    this.fContainerNumRows = -1;
    this.fAssociatedObjIDs = null;
    this.fContainerObjID = null;
    this.fHierarchyType = enumHierarchyTypes.employees;

    this.fContainerTopLinesToColumns = null;
    this.fContainerBottomLinesToColumns = null;
    this.fContainerInternalLinesToColumns = null;

    this.fAssociatedChildrenIDs = null; // to hold an order by which children of container are sorted. Needed by page optimizer
    this.fBoxRefType = enumCellRefType.topbox;

    this.fOverrides = null;
    this.unusedCellOverrides = null;
    this.fBoxContentSubType = null;

    this.fBrandReactive = false;
  }

  InitBoxLayout(boxWidth, numCols, numRows) {
    this.fBoxWidth = boxWidth;
    this.fNumCols = numCols;
    this.fNumRows = numRows;
    this.fOverrides = {};
    this.InitObjects();
  }

  GetXMLElementName() {
    return "boxlayout";
  }

  InitObjects() {
    this.fMarginMode = kStandardMargins;
    this.fBMargins = null; // ne
    this.fMgrVSpacing1 = null;
    this.fMgrVSpacing2 = null;
    this.fBSpacing = null;

    //	this.ClearSpacing();
    this.InitColumnWidths();
    //this.InitRowHeights();
    this.InitCellList();
    this.fConnectingLineDesc = new QConnectingLineFormat();
  }

  //=================================================================================================
  // Undo/Redo
  GetSCNodeRef() {
    return this.fSCNodeRef;
  }

  GetPositionRef() {
    var scNodeRef = this.GetSCNodeRef();
    if (scNodeRef) {
      return scNodeRef.GetPositionRef();
    }
    return null;
  }

  SetSCNodeRef(scNodeRef) {
    this.fSCNodeRef = scNodeRef;
  }

  Journal() {
    if (this.fSCNodeRef) this.fSCNodeRef.JournalLayoutChg(this);
  }

  Restore() {
    const scm = this.fSCNodeRef?.GetSubChartMgr?.();
    const found = scm && this.fDBObjRef ? scm.FindSCNode(this.fDBObjRef, this.fSubChartID) : null;
    const currentNode = found != null && found !== -1 ? found : this.fSCNodeRef;
    const curLayout = currentNode?.GetLayout();
    curLayout?.RestoreProperties(this);
  }

  //=================================================================================================
  // AutoText
  SetDocRef(docRef) {
    //   console.log("SetDocRef");
    this.fDocRef = docRef;
  }

  GetDocRef() {
    return this.fDocRef;
  }

  // Pie charts
  GeneratePieChartLayout() {
    var kInitialWidth = 72;
    var kTextDefSize = 12;

    this.InitBoxLayout(kInitialWidth, 1, 1);

    this.SetBorderStyle(enumElementBorderStyle.noBorder);
    this.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);
    this.SetHeightSizingMode(enumBoxHeightMode.autoself);
    this.SetFixedHeight(kInitialWidth);

    this.SetWidthMin(kInitialWidth);
    this.SetWidthMax(768);
    this.SetBoxPadding(1);
    this.SetBoxType(enumBoxType.nonstandard);

    this.SetBoxSubType(enumBoxSubTypes.refChart);
    this.SetBoxFillMode(enumFillMode.noFill);
    this.SetDocRef(this.fDocRef);

    var c1 = this.GetCellDesc(1, 1);
    c1.SetAlignment("left");
    c1.SetFontSize(kTextDefSize);
    c1.SetCellContentType(enumCellContentType.pieChart);
    c1.SetCellHeight(kInitialWidth);

    var initialVal = appstr("textbox");
    c1.SetFreeText(initialVal);

    this.CalculateLayout();
  }

  GenerateHolderAreaLayout(type) {
    const kInitialWidth = 150;
    const fillColor = "#D5D5D8";

    this.InitBoxLayout(kInitialWidth, 1, 2);
    this.SetBoxSubType(enumBoxSubTypes.holderAreaBox);
    this.SetDocRef(this.fDocRef);
    this.SetBoxShapeType(enumBoxShapes.roundRectangle);
    this.SetBoxFillMode(enumFillMode.solidFill);
    if (type !== enumEmptyPositionLayoutType.open) this.SetBoxFillColor(fillColor);
    this.SetBorderStyle(enumElementBorderStyle.noBorder);
    this.SetWidthSizingMode(enumBoxWidthMode.autosmart);
    this.SetHeightSizingMode(enumBoxHeightMode.autoself);
    this.SetWidthMin(kInitialWidth);
    this.SetBoxPadding(4);
    this.SetBoxType(enumBoxType.nonstandard);

    this.SetUpPositionExtrasCells(type);
  }

  AddPosExtraCell(x, y, contentType, freeText, imageIdx, paramters) {
    const cellDesc = this.GetCellDesc(x, y);

    cellDesc.SetCellContentType(contentType);

    if (freeText) cellDesc.SetFreeText(freeText);

    if (imageIdx) cellDesc.SetIconIndex(imageIdx);

    for (const [key, value] of Object.entries(paramters)) {
      if (key in enumUpdateProperty) {
        let processedValue = value;
        if (key === "cellFontColor") {
          processedValue = { fSelectedColor: value };
        }

        cellDesc.UpdateBoxCellDesc(processedValue, enumUpdateProperty[key], false);
      }
    }
  }

  SetUpPositionExtrasCells(layoutType) {
    const textSize = 12;
    const textColor = "#60606B";
    const kIconwidth = kExtraSmallIcon.imageWidth;
    const kIconHeight = kExtraSmallIcon.imageHeight;
    const iconFrame = { fX: kIconwidth, fY: kIconHeight };
    this.fBoxContentSubType = layoutType;

    switch (layoutType) {
      case enumEmptyPositionLayoutType.vacant:
        this.AddPosExtraCell(1, 1, enumCellContentType.icon, null, enumImageIndex.usersIcon, {
          cellTextAlign: "center",
          picFrame: iconFrame
        });
        this.AddPosExtraCell(1, 2, enumCellContentType.FreeText, appstr("Vacant"), null, {
          cellTextAlign: "center",
          fontSize: textSize,
          cellFontColor: textColor,
          cellTextBold: true
        });
        break;
      case enumEmptyPositionLayoutType.closed:
        this.AddPosExtraCell(1, 1, enumCellContentType.icon, null, enumImageIndex.userMinusIcon, {
          cellTextAlign: "center",
          picFrame: iconFrame
        });
        this.AddPosExtraCell(1, 2, enumCellContentType.FreeText, appstr("Closed"), null, {
          cellTextAlign: "center",
          fontSize: textSize,
          cellFontColor: textColor,
          cellTextBold: true
        });
        break;
      case enumEmptyPositionLayoutType.open:
        this.AddPosExtraCell(1, 1, enumCellContentType.icon, null, enumImageIndex.userPlusIcon, {
          cellTextAlign: "center",
          picFrame: iconFrame
        });
        this.AddPosExtraCell(1, 2, enumCellContentType.FreeText, appstr("Assign"), null, {
          cellTextAlign: "center",
          fontSize: textSize,
          cellFontColor: textColor,
          cellTextBold: true
        });
        break;
    }

    this.CalculateLayout();
  }

  // To be used in the unique ref where all cells belonging to the layout make use of the same property eg cell type, fieldDef
  // e.g. enumerated table
  SetCellRefType(refType) {
    this.fBoxRefType = refType;
  }

  GetCellRefType() {
    return this.fBoxRefType;
  }

  // used to tie container node to position dbObj
  SetPositionRefID(positionRefID) {
    this.fPositionRefID = positionRefID;
  }

  GetPositionRefID() {
    return this.fPositionRefID;
  }

  SetAggregatedFieldRef(fieldDef) {
    this.fFieldRef = fieldDef;
  }

  GetAggregatedFieldRef() {
    return this.fFieldRef;
  }

  // Enumerated table 'metadata' (all relevant info aside from result values)
  GetEnumeratedTableRef() {
    var fieldRef = this.GetAggregatedFieldRef();
    if (fieldRef == null) return null;
    else if (fieldRef.GetCalcCode() !== enumCalcCode.aggregatedField) return null;
    return fieldRef.GetTableRef();
  }

  BuildEmptyETLayout(errorMessage) {
    var kInitialWidth = 350;
    var defaultColCount = 8;
    var defaultRowCount = 8;

    this.InitBoxLayout(kInitialWidth, defaultColCount, defaultRowCount);
    this.SetBoxPadding(1);

    this.SetBorderStyle(enumElementBorderStyle.singleBorder);
    this.SetBoxType(enumBoxType.nonstandard);
    this.SetBoxSubType(enumBoxSubTypes.enumeratedTable);
    this.SetBoxFillMode(enumFillMode.noFill);
    this.SetDocRef(this.fDocRef);
    this.SetWidthMax(1500);
    this.SetWidthMin(kInitialWidth);
    this.SetHeightSizingMode(enumBoxHeightMode.autoself);

    // Placeholder table name
    var nameCell = SetupTableCell(this, 1, 1, "<Table Name>", "Gray5", false);
    this.DoMergeCells([nameCell, this.GetCellDesc(2, 1)]);

    // Table Rows
    SetupTableCell(this, 1, 2, "Table Rows", "Gray15", true);
    var rowMerge = [];
    for (var i = 2; i <= defaultRowCount; i++) {
      rowMerge.push(this.GetCellDesc(1, i));
      rowMerge.push(this.GetCellDesc(2, i));
    }
    this.DoMergeCells(rowMerge);

    // Table Columns
    SetupTableCell(this, 3, 1, "Table Columns", "Gray15", true);
    var colMerge = [];
    for (var i = 3; i <= defaultColCount; i++) {
      colMerge.push(this.GetCellDesc(i, 1));
    }
    this.DoMergeCells(colMerge);

    // Result row
    var resultRow = SetupTableCell(
      this,
      3,
      defaultRowCount,
      "Results",
      "Gray5",
      true,
      false,
      9,
      true
    );
    resultRow.SetAlignment("center");
    var resultMerge = [];
    for (var i = 3; i <= defaultColCount; i++)
      resultMerge.push(this.GetCellDesc(i, defaultRowCount));
    this.DoMergeCells(resultMerge);

    // results info
    var textVal = errorMessage
      ? errorMessage.replaceAll("^", " ")
      : "<Add an Aggregated Field to Begin>";
    var valueCell = SetupTableCell(this, 3, 2, textVal, null, false);
    valueCell.SetAlignment("center");
    valueCell.SetWrapText(enumWrapTextMode.unlimited);
    var resultCells = [];
    for (var i = 3; i <= defaultColCount; i++) {
      for (var j = 2; j < defaultRowCount; j++) {
        resultCells.push(this.GetCellDesc(i, j));
      }
    }
    this.DoMergeCells(resultCells);

    this.CalculateLayout();
  }

  // Generating the layout based on the metadata from the ET associated with this table
  // this needs... a lot of cleanup when it comes to populating the header value cells.
  GenerateEnumeratedTableLayout() {
    var kInitialWidth = 800;
    var tableInfo = this.GetEnumeratedTableRef();
    if (tableInfo == null) {
      // aggregated field associated with this table was either deleted, or excluded
      this.BuildEmptyETLayout();
      return;
    } else if (
      tableInfo["overLimit"] ||
      tableInfo["rowAggregationResult"] == null ||
      tableInfo["columnAggregationResult"] == null
    ) {
      // table calc wasn't run
      var errorMessage = tableInfo["message"]
        ? tableInfo["message"]
        : "Failed to process formula. Try reducing the number of rows and columns.";
      this.BuildEmptyETLayout(errorMessage);
      return;
    } else if (this.GetDocRef() == null) {
      return;
    }

    var rowCount = tableInfo["rowIdx"];
    var columnCount = tableInfo["columnIdx"];

    //this.CalculateLayout();
    var tableColumns = columnCount + this.GetValColStartIdx() - 1;
    var tableRows = rowCount + this.GetValRowStartIdx();
    if (tableInfo["rows"].length < 1 || tableInfo["results"].length < 1) tableRows--;
    this.InitBoxLayout(kInitialWidth, tableColumns, tableRows);
    this.fHeightSizingMode = enumBoxHeightMode.autoself;

    var objDB = this.GetDocRef().GetObjDB();
    var classDef = objDB.GetClassByName("Box");

    var rowDefs = tableInfo["rows"].map(function (aRow) {
      return classDef.GetFieldDefByName(aRow);
    });

    var columnDefs = tableInfo["columns"].map(function (aColumn) {
      return classDef.GetFieldDefByName(aColumn);
    });

    SetupTableCell(this, 1, 1, tableInfo["name"], "Gray15", true, enumWrapTextMode.twolines); // table name cell
    var labelMerge = [];
    for (var i = 1; i <= tableInfo["rows"].length; i++) {
      labelMerge.push(this.GetCellDesc(i, 1));
    }
    this.DoMergeCells(labelMerge);

    // Row Cell handling (HEADERS)
    // Row headers are oriented left -> right
    var rowIdx = columnDefs.length > 1 ? columnDefs.length : 2;
    for (var i = 0; i < rowDefs.length; i++) {
      var aRowDef = rowDefs[i];
      SetupTableCell(
        this,
        i + 1,
        rowIdx,
        aRowDef.GetFieldLabel(),
        kPickerColorCodes[2 - (i % 2)],
        true,
        enumWrapTextMode.twolines,
        9,
        true
      );
    }
    // Column Cell Handling (HEADERS)
    // Column headers are oriented top -> bottom
    var colIdx = rowDefs.length > 1 ? rowDefs.length + 1 : 2;
    for (var i = 0; i < columnDefs.length; i++) {
      var aColumnDef = columnDefs[i];
      SetupTableCell(
        this,
        colIdx,
        i + 1,
        aColumnDef.GetFieldLabel(),
        kPickerColorCodes[2 - (i % 2)],
        true,
        enumWrapTextMode.twolines,
        9
      );
    }

    // row value population under row headers
    // this... needs serious cleanup and consolidation w/ col value population + broken out into its own fxn
    rowIdx += 1;
    for (var i = 0; i < tableInfo["rowAggregationResult"].length; i++) {
      var toMerge = [];
      var aResult = tableInfo["rowAggregationResult"][i];
      var outerVal = aResult["_id"][tableInfo["rows"][0]];
      if (rowDefs.length === 1) outerVal = Object.keys(outerVal)[0];
      SetupTableCell(
        this,
        1,
        rowIdx,
        outerVal,
        kPickerColorCodes[8 + 1 + (1 - (i % 2)) * 2],
        false,
        enumWrapTextMode.twolines,
        8,
        true
      );
      var secondLevel = aResult[tableInfo["rows"][1]];
      if (secondLevel == null)
        // 1 row
        rowIdx += 1;
      else {
        if (rowDefs.length == 2) {
          // dictionaries are an unordered data structure. need to sort the values of the 'deepest' header by index here
          // otherwise value cells will be populated for incorrect header values
          var orderedSecond = Object.keys(secondLevel).map(function (key) {
            return [key, secondLevel[key]];
          });
          orderedSecond.sort(function (first, second) {
            return first[1] - second[1];
          });
          for (var anIdx in orderedSecond) {
            var aVal = orderedSecond[anIdx][0];
            toMerge.push(this.GetCellDesc(1, rowIdx));
            SetupTableCell(
              this,
              2,
              rowIdx,
              aVal,
              kPickerColorCodes[8 + 1 + (1 - (i % 2)) * 2],
              false,
              enumWrapTextMode.twolines,
              8,
              true
            );
            rowIdx += 1;
          }
        } else if (rowDefs.length == 3) {
          for (var objIdx in secondLevel) {
            var innerMerge = [];
            var secondLevelObj = secondLevel[objIdx];
            var secondLevelVal = secondLevelObj[tableInfo["rows"][1]];
            SetupTableCell(
              this,
              2,
              rowIdx,
              secondLevelVal,
              kPickerColorCodes[8 + 1 + (1 - (i % 2)) * 2],
              false,
              enumWrapTextMode.twolines,
              8
            );
            var thirdLevel = secondLevelObj[tableInfo["rows"][2]];
            var orderedThird = Object.keys(thirdLevel).map(function (key) {
              return [key, thirdLevel[key]];
            });
            orderedThird.sort(function (first, second) {
              return first[1] - second[1];
            });
            for (var anIdx in orderedThird) {
              var aVal = orderedThird[anIdx][0];
              toMerge.push(this.GetCellDesc(1, rowIdx));
              innerMerge.push(this.GetCellDesc(2, rowIdx));
              SetupTableCell(
                this,
                3,
                rowIdx,
                aVal,
                kPickerColorCodes[8 + 1 + (1 - (i % 2)) * 2],
                false,
                enumWrapTextMode.twolines,
                8,
                true
              );
              rowIdx += 1;
            }
            this.DoMergeCells(innerMerge);
          }
        }
      }

      this.DoMergeCells(toMerge);
    }

    // column value population right of column headers
    colIdx += 1;
    for (var i = 0; i < tableInfo["columnAggregationResult"].length; i++) {
      toMerge = [];

      var aResult = tableInfo["columnAggregationResult"][i]; // aggregation result for first header in columns list
      var outerVal = aResult["_id"][tableInfo["columns"][0]];
      if (columnDefs.length === 1) outerVal = Object.keys(outerVal)[0];
      if (outerVal == "") outerVal = "<Blank>";
      SetupTableCell(
        this,
        colIdx,
        1,
        outerVal,
        kPickerColorCodes[3 + 1 - (i % 2)],
        false,
        enumWrapTextMode.twolines,
        9
      );
      var secondLevel = aResult[tableInfo["columns"][1]];
      if (secondLevel == null)
        // 1 col
        colIdx += 1;
      else {
        if (columnDefs.length === 2) {
          // dictionaries are an unordered data structure. need to sort the values of the 'deepest' header by index here
          // otherwise value cells will be populated for incorrect header values
          var orderedSecond = Object.keys(secondLevel).map(function (key) {
            return [key, secondLevel[key]];
          });
          orderedSecond.sort(function (first, second) {
            return first[1] - second[1];
          });

          for (var anIdx in orderedSecond) {
            toMerge.push(this.GetCellDesc(colIdx, 1));
            aVal = orderedSecond[anIdx][0];
            SetupTableCell(
              this,
              colIdx,
              2,
              aVal,
              kPickerColorCodes[3 + 1 - (i % 2)],
              false,
              enumWrapTextMode.twolines,
              8
            );
            colIdx += 1;
          }
        } else if (columnDefs.length === 3) {
          for (var objIdx in secondLevel) {
            var innerMerge = [];
            var secondLevelObj = secondLevel[objIdx];
            var secondLevelVal = secondLevelObj[tableInfo["columns"][1]];
            SetupTableCell(this, colIdx, 2, secondLevelVal, kPickerColorCodes[5 + 2], true);
            var thirdLevel = secondLevelObj[tableInfo["columns"][2]];
            var orderedThird = Object.keys(thirdLevel).map(function (key) {
              return [key, thirdLevel[key]];
            });
            orderedThird.sort(function (first, second) {
              return first[1] - second[1];
            });
            for (var anIdx in orderedThird) {
              toMerge.push(this.GetCellDesc(colIdx, 1));
              innerMerge.push(this.GetCellDesc(colIdx, 2));
              aVal = orderedThird[anIdx][0];
              SetupTableCell(
                this,
                colIdx,
                3,
                aVal,
                kPickerColorCodes[3 + 1 - (i % 2)],
                false,
                enumWrapTextMode.twolines,
                8
              );
              colIdx += 1;
            }
            this.DoMergeCells(innerMerge);
          }
        }
        this.DoMergeCells(toMerge);
      }
    }
    // results label
    if (tableInfo["rows"].length > 0 && tableInfo["results"].length > 0) {
      SetupTableCell(
        this,
        1,
        this.fNumRows,
        appstr("results"),
        kPickerColorCodes[2],
        true,
        false,
        9,
        true
      );
      var toMerge = [];
      for (var i = 1; i <= tableInfo["rows"].length; i++) {
        toMerge.push(this.GetCellDesc(i, this.fNumRows));
      }
      this.DoMergeCells(toMerge);
    }

    // Set cell types for table counts & calcs
    // If table results are shifted, it is because something went wrong here.
    this.InitTableValCells(tableInfo);

    this.CalculateLayout();
  }

  GetValColStartIdx() {
    var tableInfo = this.GetEnumeratedTableRef();
    var colIdx = tableInfo["rows"].length > 1 ? tableInfo["rows"].length + 1 : 2;
    if (tableInfo["columns"].length > 0)
      // shift past empty col
      colIdx += 1;
    return colIdx;
  }

  GetValRowStartIdx() {
    var tableInfo = this.GetEnumeratedTableRef();
    return tableInfo["columns"].length > 1 ? tableInfo["columns"].length + 1 : 3;
  }

  InitTableValCells(tableInfo) {
    var numCols = this.fNumCols;
    var numRows = this.fNumRows;

    var startCol = this.GetValColStartIdx();
    var startRow = this.GetValRowStartIdx();

    var hasResultRow = false;
    var isPercent = false;
    if (tableInfo["rows"].length > 0 && tableInfo["results"] && tableInfo["results"].length > 0) {
      hasResultRow = true;
      isPercent = tableInfo["results"][0]["percent"];
    }

    for (var colIdx = startCol; colIdx <= numCols; colIdx++) {
      for (var rowIdx = startRow; rowIdx <= numRows; rowIdx++) {
        var aCell = this.GetCellDesc(colIdx, rowIdx);
        aCell.SetFontSize(8);
        aCell.SetAlignment("right");
        if (rowIdx == numRows && hasResultRow) {
          if (isPercent) aCell.SetCellContentType(enumCellContentType.tablePercent);
          else aCell.SetCellContentType(enumCellContentType.tableSum);
        } else {
          //aCell.SetCellFillMode(enumFillMode.solidFill);
          //aCell.SetCellFillColor(kPickerColorCodes[kRed]);
          aCell.SetCellContentType(enumCellContentType.tableValue);
        }
      }
    }
  }

  GetTableValueByIndices(cellRef, colIdx, rowIdx) {
    // getting cell display content
    var tableData = this.GetEnumeratedTableValues(cellRef);
    if (tableData == null) return null;
    var colStartIdx = this.GetValColStartIdx();
    var rowStartIdx = this.GetValRowStartIdx();
    return tableData[rowIdx - rowStartIdx][colIdx - colStartIdx]; // backend matrix does not factor in frontend index shifting. need to unshift here.
  }

  GetEnumeratedTableValues(cellRef) {
    // getting cell display content
    var tableFieldDef = this.GetAggregatedFieldRef();
    if (tableFieldDef == null) return null;
    var topObj = cellRef.GetTopBoxRef();
    if (topObj == null) return null;
    var recordDetails = topObj.fRecordDetails; // ugly
    if (recordDetails == null) return null;
    if (recordDetails[tableFieldDef.GetFieldName()] == null) return null;
    return recordDetails[tableFieldDef.GetFieldName()];
  }

  BuildValueMatrix(details) {
    var tableInfo = this.GetEnumeratedTableRef();
    if (tableInfo == null || details == null) {
      return null;
    }
    var rowCount = tableInfo["rowIdx"] + 1;
    var columnCount = tableInfo["columnIdx"] + 1;
    // do we really need this full array?
    var tableValues = [...Array(rowCount)].map(x => Array(columnCount).fill(0)); // initialize matrix (fill w/ 0)
    for (var i = 0; i < details.length; i++) {
      var valueTup = details[i];
      tableValues[valueTup[0]][valueTup[1]] = valueTup[2];
    }
    return tableValues;
  }

  GetValueColorMap() {
    return this.fValueColorMap;
  }

  SetValueColorMap(colorMap) {
    this.fValueColorMap = colorMap;
  }

  //=================================================================================================
  // Clone - Create a copy of a BoxLayout
  Clone(options = {}) {
    var { includeId, includeLayoutName, includeBoxStyleID } = options;

    var c = new QBoxLayout();
    c.fDBObjRef = this.fDBObjRef;
    c.fObjClassName = this.fObjClassName;
    c.fBoxWidth = this.fBoxWidth;
    c.fNumCols = this.fNumCols;
    c.fNumRows = this.fNumRows;
    c.fHighlightInfo = this.fHighlightInfo;
    c.fBoxBorderColor = this.fBoxBorderColor;
    c.fShowShadow = this.fShowShadow;
    c.fConnectorDashStyle = this.fConnectorDashStyle;
    c.fConnectorWeightStyle = this.fConnectorWeightStyle;
    c.fConnectorColor = this.fConnectorColor;

    c.fBranchStyle = this.fBranchStyle;
    c.fBranchGroup = this.fBranchGroup;

    c.fBoxShapeType = this.fBoxShapeType;
    c.fBoxFillMode = this.fBoxFillMode;
    c.fBoxFillColor = this.fBoxFillColor;
    c.fBoxFillColorGradient = this.fBoxFillColorGradient;

    c.fBorderThickness = this.fBorderThickness;
    c.fBorderStyle = this.fBorderStyle;
    c.fDashStyle = this.fDashStyle;
    c.fBrandReactive = this.fBrandReactive;

    c.fConnectingLineDesc = this.fConnectingLineDesc.Clone();

    c.fPositionX = this.fPositionX;
    c.fPositionY = this.fPositionY;

    //c.fBoxWidth = this.fBoxWidth;
    c.fWidthSizingMode = this.fWidthSizingMode;
    c.fWidthMin = this.fWidthMin;
    c.fWidthMax = this.fWidthMax;

    c.fHeightSizingMode = this.fHeightSizingMode;
    c.fFixedHeight = this.fFixedHeight;
    c.fSmartHeight = -1;

    c.fContentAlign = this.fContentAlign;

    c.fCenterChartHorz = this.fCenterChartHorz;
    c.fCenterChartVert = this.fCenterChartVert;

    c.fHorzPositioning = this.fHorzPositioning;
    c.fVertPositioning = this.fVertPositioning;
    c.fDistRight = this.fDistRight;
    c.fDistBottom = this.fDistBottom;

    c.fBoxPadding = this.fBoxPadding;

    c.fBoxType = this.fBoxType;
    c.fBoxSubType = this.fBoxSubType;
    c.fBoxRefType = this.fBoxRefType;

    c.fFieldName = this.fFieldName;
    c.fFieldID = this.fFieldID;
    c.fFieldRef = this.fFieldRef;
    c.fTableValues = this.fTableValues;

    c.fValueColorMap = this.fValueColorMap;

    c.fLayer = this.fLayer;
    c.fSCNodeRef = this.fSCNodeRef;

    c.fSubChartFlag = this.fSubChartFlag;
    c.fSubChartID = this.fSubChartID;

    c.fContainerNumCols = this.fContainerNumCols;
    c.fContainerType = this.fContainerType;
    c.fContainerNumRows = this.fContainerNumRows;
    c.fContainerObjID = this.fContainerObjID;
    c.fContainerTopLinesToColumns = this.fContainerTopLinesToColumns;
    c.fContainerBottomLinesToColumns = this.fContainerBottomLinesToColumns;
    c.fContainerInternalLinesToColumns = this.fContainerInternalLinesToColumns;
    c.fHierarchyType = this.fHierarchyType;

    c.fAssociatedObjIDs = this.fContainerType === enumContainerType.standardmrb ? [] : null;
    if (this.fAssociatedObjIDs) {
      c.ClearAssociatedDBObjs();
      var n = this.fAssociatedObjIDs.length;
      for (var i = 0; i < n; i++) c.fAssociatedObjIDs.push(this.fAssociatedObjIDs[i]);
    }
    c.fPositionRefID = this.fPositionRefID;
    if (this.fBMargins) c.fBMargins = this.fBMargins.Clone();
    if (this.fBSpacing) c.fBSpacing = this.fBSpacing.Clone();

    c.fMgrVSpacing1 = this.fMgrVSpacing1;
    c.fMgrVSpacing2 = this.fMgrVSpacing2;

    c.fMarginMode = this.fMarginMode;

    c.fFlagType = this.fFlagType;
    c.fFlagColor1 = this.fFlagColor1;
    c.fFlagColor2 = this.fFlagColor2;
    c.fFlagColor3 = this.fFlagColor3;
    c.fTriPosition = this.fTriPosition;

    c.fFlagLabel = this.fFlagLabel;

    c.fColWidths = null;
    if (this.fColWidths) {
      c.fColWidths = [];
      var n = this.fColWidths.length;
      for (var i = 0; i < n; i++) c.fColWidths.push(this.fColWidths[i]);
    }

    /*
	c.fRowHeights = null;
	if (this.fRowHeights)
	{
		c.fRowHeights = new Array();
		var n = this.fRowHeights.length;
		for (var i=0; i<n; i++)
			c.fRowHeights.push(this.fRowHeights[i]);
	}
	*/

    c.fCellList = null;
    if (this.fCellList) {
      c.fCellList = [];
      var n = this.fCellList.length;
      for (var i = 0; i < n; i++) {
        var cellDescClone = this.fCellList[i].Clone();
        cellDescClone.SetParentLayout(c);
        c.fCellList.push(cellDescClone);
      }
    }

    c.fUseCalcWidth = this.fUseCalcWidth;
    c.fDocRef = this.fDocRef;

    c.fOverrides = { ...this.fOverrides };
    if (this.unusedCellOverrides) c.unusedCellOverrides = this.unusedCellOverrides;

    if (includeId) c.fObjID = this.fObjID;
    if (includeLayoutName) c.fBoxStyleName = this.fBoxStyleName;
    if (includeBoxStyleID) c.fBoxStyleID = this.fBoxStyleID;

    c.fBoxContentSubType = this.fBoxContentSubType;

    return c;
  }

  CloneLib(srcLayout) {
    var c = this.Clone();
    c.fObjID = srcLayout.fObjID;
    c.fSubChartID = srcLayout.fSubChartID;
    c.fSubChartFlag = srcLayout.fSubChartFlag;
    c.fContainerObjID = srcLayout.fContainerObjID;

    c.fObjClassName = srcLayout.fObjClassName;
    c.fLockFlag = srcLayout.fLockFlag;

    c.TransferBranchStyle(srcLayout);
    c.TransferMarginsAndSpacing(srcLayout);
    c.CopyCentering(srcLayout);
    //1.9.2FIX
    c.CopyPositioning(srcLayout);

    return c;
  }

  CopyCentering(srcLayout) {
    this.fCenterChartHorz = srcLayout.fCenterChartHorz;
    this.fCenterChartVert = srcLayout.fCenterChartVert;
    this.fHorzPositioning = srcLayout.fHorzPositioning;
    this.fVertPositioning = srcLayout.fVertPositioning;

    this.fDistRight = srcLayout.fDistRight;
    this.fDistBottom = srcLayout.fDistBottom;
  }

  //1.9.2FIX
  CopyPositioning(srcLayout) {
    if (
      this.fCenterChartHorz == enumHorzCentering.userplacement ||
      this.fCenterChartVert == enumVertCentering.userplacement
    ) {
      this.fPositionX = srcLayout.fUserPositionX;
      this.fPositionY = srcLayout.fUserPositionY;
    } else {
      var x = srcLayout.fPositionX;
      var y = srcLayout.fPositionY;
      if (x == null) x = 0;
      this.fPositionX = x;
      if (y == null) y = 0;
      this.fPositionY = y;
    }
  }

  //=================================================================================================
  // Load/Save Methods
  GetContainerDataObjs() {
    //TODO: I don't know it this is the most elegant way of looking up the root data object (for containers).
    //We will probably need a way to have containers be linked to an actual data object too instead of
    if (this.GetDBObj()) return [this.GetDBObj()];
    else if (this.fAssociatedObjIDs && this.fAssociatedObjIDs.length > 0) {
      var doc = this.GetDocRef();
      if (!doc) {
        console.log("This should not happen.");
        return;
      }
      return this.fAssociatedObjIDs.map(function (a) {
        return doc.GetBoxObjByID(a);
      });
    }
    return null;
  }

  GetDBObj() {
    return this.fDBObjRef;
  }

  SetDBObj(dbObj) {
    this.fDBObjRef = dbObj;
    //if (dbObj != null)
    //this.SetDBObjID(dbObj.GetObjectID())
  }

  // When we go to persistent storage - we need an ID so we can link to DBObj on load
  // We also use the ID for MRB where it matters to keep it updated during runtime
  GetDBObjID() {
    return this.fObjID;
  }

  SetDBObjID(objID) {
    this.fObjID = objID;
  }

  GetSubChartID() {
    return this.fSubChartID;
  }

  SetSubChartID(subChartID) {
    this.Journal();
    this.fSubChartID = subChartID;
  }

  GetSubChartFlag() {
    return this.fSubChartFlag;
  }

  SetSubChartFlag(flag) {
    this.Journal();
    this.fSubChartFlag = flag;
  }

  GetCollapsed() {
    return this.fCollapsed;
  }

  SetCollapsed(isCollapsed) {
    this.fCollapsed = isCollapsed;
  }

  GetHidden() {
    return this.fHidden;
  }

  SetHidden(isHidden) {
    this.fHidden = isHidden;
  }

  CopySubChartInfo(srcLayout) {
    this.fSubChartID = srcLayout.fSubChartID;
    this.fSubChartFlag = srcLayout.fSubChartFlag;
  }

  GetDBObjClassName() {
    return this.fObjClassName;
  }

  SetDBObjClassName(className) {
    this.fObjClassName = className;
  }

  GetLockFlag() {
    return this.fLockFlag;
  }

  SetLockFlag(lockFlag) {
    if (lockFlag) this.fLockFlag = 1;
    else this.fLockFlag = 0;
  }

  SetTopOptions(hasDrillup) {
    //Generic fxn for setting the common attributes for top box.
    this.SetChartHorzCentering(enumHorzCentering.centermiddle);
    this.SetChartVertCentering(enumVertCentering.centertop);
    if (hasDrillup === true) this.SetSubChartFlag(enumSubChartFlag.drillup);
  }

  //=======================================================================================
  // MRB
  ClearAssociatedDBObjs() {
    this.fAssociatedObjIDs = [];
    this.fAssociatedChildrenIDs = [];
  }

  GetContainerNumCols() {
    return this.fContainerNumCols;
  }

  SetContainerNumCols(numCols) {
    this.Journal();
    this.fContainerNumCols = numCols;
  }

  GetContainerMaxRows() {
    return this.fContainerNumRows;
  }

  SetContainerMaxRows(maxRows) {
    this.Journal();
    this.fContainerNumRows = maxRows;
  }

  GetContainerTopLinesToColumns() {
    return this.fContainerTopLinesToColumns;
  }

  SetContainerTopLinesToColumns(doTopLines) {
    this.Journal();
    this.fContainerTopLinesToColumns = doTopLines;
  }

  GetContainerBottomLinesToColumns() {
    return this.fContainerBottomLinesToColumns;
  }

  SetContainerBottomLinesToColumns(doBottomLines) {
    this.Journal();
    this.fContainerBottomLinesToColumns = doBottomLines;
  }

  GetContainerInternalLinesToColumns() {
    return this.fContainerInternalLinesToColumns;
  }

  SetContainerInternalLinesToColumns(doInternalLines) {
    this.Journal();
    this.fContainerInternalLinesToColumns = doInternalLines;
  }

  GetContainerType() {
    return this.fContainerType;
  }

  SetContainerType(containerType) {
    this.Journal();
    this.fContainerType = containerType;
  }

  GetHierarchyType() {
    return this.fHierarchyType;
  }

  SetHierarchyType(hierarchyType) {
    this.Journal();
    this.fHierarchyType = hierarchyType;
  }

  GetContainerEnvelope() {
    return this.fContainerEnvelope;
  }

  SetContainerEnvelope(containerEnvelope) {
    this.fContainerEnvelope = containerEnvelope;
  }

  GetAssociatedObjIDs(cloneIt) {
    var theList = this.fAssociatedObjIDs;
    if (cloneIt && theList != null) theList = theList.slice();

    return theList;
  }

  SetAssociatedObjIDs(assocObjIDs) {
    this.Journal();
    this.fAssociatedObjIDs = assocObjIDs;
  }

  SetContainerObjID(containerObjID) {
    this.Journal();
    this.fContainerObjID = containerObjID;
  }

  GetContainerObjID() {
    return this.fContainerObjID;
  }

  GetAssociatedChildrenIDs() {
    if (this.fAssociatedChildrenIDs) {
      return this.fAssociatedChildrenIDs;
    }

    return [];
  }

  AddAssociatedChildID(nodeID) {
    if (!this.fAssociatedChildrenIDs) {
      this.fAssociatedChildrenIDs = [];
    }

    // making sure the ID is not duplicated
    this.RemoveAssociatedChildID(nodeID);
    this.fAssociatedChildrenIDs.push(nodeID);
  }

  RemoveAssociatedChildID(nodeID) {
    if (!this.fAssociatedChildrenIDs || this.fAssociatedChildrenIDs.length === 0) {
      return;
    }

    var foundIndex = this.fAssociatedChildrenIDs.indexOf(nodeID);
    if (foundIndex > -1) {
      this.fAssociatedChildrenIDs.splice(foundIndex, 1);
    }
  }

  ReplaceAssociatedChildrenIDs(IDs) {
    if (!this.fAssociatedChildrenIDs) {
      this.fAssociatedChildrenIDs = [];
    }

    // first remove existing IDs if they are in the list to avoid duplicates
    for (var i = 0; i < IDs.length; i++) {
      var foundIndex = this.fAssociatedChildrenIDs.indexOf(IDs[i]);
      if (foundIndex > -1) {
        this.fAssociatedChildrenIDs.splice(foundIndex, 1);
      }
    }

    this.fAssociatedChildrenIDs = this.fAssociatedChildrenIDs.concat(IDs);
  }

  GetHighlightInfo() {
    if (this.fHighlightInfo == null) this.fHighlightInfo = {};

    return this.fHighlightInfo;
  }

  OutputStdElements(stream) {
    if (this.fMarginMode != kStandardMargins)
      this.OutputSimpleElement("bmargmode", this.fMarginMode, stream);

    if (this.fBMargins)
      this.OutputElementQuad(
        "bmarg",
        this.fBMargins.fLeft,
        this.fBMargins.fTop,
        this.fBMargins.fRight,
        this.fBMargins.fBottom,
        stream
      );
    if (this.fMgrVSpacing1 != null && this.fMgrVSpacing2 != null) {
      var v1 = this.fMgrVSpacing1;
      if (this.fMgrVSpacing1 == null) v1 = 0;
      var v2 = this.fMgrVSpacing2;
      if (this.fMgrVSpacing2 == null) v2 = 0;
      this.OutputElementPair("bmgrspace", v1, v2, stream);
    }

    if (this.fBSpacing)
      this.OutputElementQuad(
        "bspacing",
        this.fBSpacing.fLeft,
        this.fBSpacing.fTop,
        this.fBSpacing.fRight,
        this.fBSpacing.fBottom,
        stream
      );

    var defaultCC =
      this.fCenterChartHorz == enumHorzCentering.userplacement &&
      this.fCenterChartVert == enumVertCentering.userplacement;
    if (!defaultCC)
      this.OutputElementPair("cc", this.fCenterChartHorz, this.fCenterChartVert, stream);

    var defaultCP = IsDefaultCP(this);
    if (!defaultCP)
      this.OutputElementQuad(
        "cp",
        this.fHorzPositioning,
        this.fVertPositioning,
        this.fDistRight,
        this.fDistBottom,
        stream
      );

    //7  this.OutputElementPair("chartcentering", this.fCenterChartHorz, this.fCenterChartVert ,stream);
    //8  this.OutputElementQuad("positioning", this.fHorzPositioning, this.fVertPositioning, this.fDistRight, this.fDistBottom, stream);

    if (
      this.fCenterChartHorz == enumHorzCentering.userplacement ||
      this.fCenterChartVert == enumVertCentering.userplacement
    ) {
      if (this.fPositionX != 0 || this.fPositionY != 0) {
        this.OutputElementPair("up", this.fPositionX, this.fPositionY, stream);
        //9  this.OutputElementPair("userposition", this.fPositionX,this.fPositionY ,stream);
      }
    }
  }

  OutputSubElements(stream) {
    // Library
    var libID = this.GetLibID();
    if (libID != null) this.OutputSimpleElementF("libID", libID, stream);
    else {
      this.OutputSimpleElementF("di", this.fObjID, stream);

      var scFlag = this.fSubChartFlag;
      var dbObj = this.GetDBObj();
      if (dbObj && dbObj["ignoreSCTop"] == true) scFlag = enumSubChartFlag.nobreak;

      this.OutputElementPairF("si", this.fSubChartID, scFlag, stream);
      this.OutputSimpleElementF("dc", this.fObjClassName, stream);
      this.OutputElementPairF("bs", this.fBranchStyle, this.fBranchGroup, stream);

      if (this.fContainerType != enumContainerType.nonmrb) {
        var top = this.fContainerTopLinesToColumns ? 1 : 0;
        var bottom = this.fContainerBottomLinesToColumns ? 1 : 0;
        var internal = this.fContainerInternalLinesToColumns ? 1 : 0;
        this.OutputElementQuin(
          "mr",
          this.fContainerNumCols,
          this.fContainerNumRows,
          top,
          bottom,
          internal,
          stream
        );

        if (this.fAssociatedObjIDs != null && this.fAssociatedObjIDs.length > 0)
          this.OutputElementList("ma", this.fAssociatedObjIDs, stream);
      }
      if (this.GetContainerObjID()) this.OutputSimpleElement("ctnr", this.fContainerObjID, stream);
      if (this.fLockFlag == 1) {
        this.OutputSimpleElement("lf", this.fLockFlag, stream);
        //5 this.OutputSimpleElement("lockflag", this.fLockFlag, stream);
      }
      this.OutputStdElements(stream);

      // Load from Library
      var libRefID = this.GetLibRefID();
      if (libRefID != null) {
        this.OutputSimpleElementF("li", libRefID, stream);
        //6 this.OutputSimpleElement("libRefID", libRefID, stream);
        return;
      }
    }

    this.OutputElementQuad(
      "boxwidth",
      this.fBoxWidth,
      this.fWidthSizingMode,
      this.fWidthMin,
      this.fWidthMax,
      stream
    );

    var fixedHeight = -1;
    if (this.fHeightSizingMode != enumBoxHeightMode.autoself) fixedHeight = this.GetFixedHeight(); // fFixedHeight;
    this.OutputElementPair("boxheight", fixedHeight, this.fHeightSizingMode, stream);

    this.OutputElementPair("boxinfo", this.fBoxType, this.fLayer, stream);
    this.OutputSimpleElement("boxsubtype", this.fBoxSubType, stream);

    this.OutputSimpleElement("contentalign", this.fContentAlign, stream);
    //	this.OutputElementPair("branchstyle", this.fBranchStyle, this.fBranchGroup ,stream);
    this.OutputElementQuad(
      "positioninfo",
      this.fNumCols,
      this.fNumRows,
      this.fPositionX,
      this.fPositionY,
      stream
    );
    this.OutputElementQuad(
      "boxprops",
      this.fBoxShapeType,
      this.fShowShadow,
      this.fBoxFillColor,
      this.fBoxFillMode,
      stream
    );
    var tableField = this.GetAggregatedFieldRef();
    if (tableField != null) {
      var fieldName = tableField.GetFieldName();
      var fieldID = tableField.GetFieldID();
      this.OutputSimpleElement("fieldid", fieldID, stream);
      this.OutputSimpleElement("fieldname", fieldName, stream);
      var boxRefType = this.GetCellRefType();
      if (boxRefType != enumCellRefType.topbox)
        this.OutputSimpleElement("boxreftype", boxRefType, stream);
    }
    if (this.fBoxFillColorGradient != "#FFFFFF")
      this.OutputSimpleElement("boxprops2", this.fBoxFillColorGradient, stream);

    if (this.fPositionRefID != null)
      this.OutputSimpleElement("positionRefID", this.fPositionRefID, stream);

    this.OutputElementQuad(
      "borderprops",
      this.fBorderThickness,
      this.fBorderStyle,
      this.fBoxBorderColor,
      this.fBoxPadding,
      stream
    );

    if (this.fConnectorDashStyle != null)
      this.OutputElementList(
        "linedash",
        [this.fConnectorDashStyle, this.fConnectorWeightStyle],
        stream
      );
    if (this.fConnectorWeightStyle != null)
      this.OutputSimpleElement("lineweight", this.fConnectorWeightStyle, stream);
    if (this.fConnectorColor != null)
      this.OutputSimpleElement("linecolor", this.fConnectorColor, stream);
    if (this.fDashStyle != enumDashStyle.nodash)
      this.OutputSimpleElement("boxdash", this.fDashStyle, stream);

    if (this.fFlagType != enumFlagType.none)
      this.OutputElementQuin(
        "annoflag",
        this.fFlagType,
        this.fFlagLabel,
        this.fFlagColor1,
        this.fFlagColor2,
        this.fFlagColor3,
        stream
      );

    if (this.fTriPosition != 0) this.OutputSimpleElement("tripos", this.fTriPosition, stream);

    if (this.fHighlightInfo && JSON.stringify(this.fHighlightInfo) !== "{}") {
      var highlightInfo = this.fHighlightInfo;
      if (highlightInfo["template"]) {
        var highlightInfoJSON = JSON.stringify({
          template: this.fHighlightInfo["template"]
        });
        this.OutputSimpleElement("highlight", highlightInfoJSON, stream);
      }
    }

    this.OutputElementList("colwidth", this.fColWidths, stream);

    var collapsedState = this.fCollapsed ? 0 : 1;
    this.OutputSimpleElement("collapsed", collapsedState, stream);

    var t = this.fBoxStyleName;
    if (t != null) {
      this.OutputSimpleElement("template", t, stream);
      var tID = this.fBoxStyleID;
      this.OutputSimpleElement("templateID", tID, stream);
    }
  }

  GetFieldSaveName() {
    return this.fFieldName;
  }

  GetFieldSaveID() {
    return this.fFieldID;
  }

  HandleLayoutField(objDB) {
    var fieldSaveName = this.GetFieldSaveName();
    if (this.GetFieldSaveName() != null) {
      var objClass = objDB.GetClassByName("Box");
      var fieldDef = objClass.GetFieldDefByName(fieldSaveName);
      if (fieldDef == null) fieldDef = objClass.GetFieldDefByName(this.GetFieldSaveID());
      this.SetAggregatedFieldRef(fieldDef);
      if (this.GetDocRef() && this.GetBoxSubType() === enumBoxSubTypes.enumeratedTable)
        // mp and doc have been joined
        this.GenerateEnumeratedTableLayout();
    }
  }

  LoadCenteringInfo(objTree, version) {
    var valueList = null;

    var s1 = "cc";
    var s2 = "up";
    if (version == 1) {
      s1 = "chartcentering";
      s2 = "userposition";
    }

    valueList = objTree.GetValueListForChild(s1, true);
    if (valueList == null && version > 1) {
      valueList = new Array(2);
      valueList[0] = enumHorzCentering.userplacement;
      valueList[1] = enumVertCentering.userplacement;
    }

    var resetCenteringInfo = true;
    if (valueList) {
      this.fCenterChartHorz = valueList[0];
      this.fCenterChartVert = valueList[1];
      if (
        this.fCenterChartHorz == enumHorzCentering.userplacement ||
        this.fCenterChartVert == enumVertCentering.userplacement
      ) {
        valueList = objTree.GetValueListForChild(s2, false);
        if (valueList) {
          var v1 = valueList[0];
          var v2 = valueList[1];
          if (v1 != "null" && v2 != "null") {
            if (v1 != "NaN" && v2 != "NaN") {
              this.fUserPositionX = parseInt(v1);
              this.fUserPositionY = parseInt(v2);
              resetCenteringInfo = false;
            }
          }
        }
      } else resetCenteringInfo = false;
    }

    if (resetCenteringInfo) {
      this.fCenterChartHorz = enumHorzCentering.centermiddle;
      this.fCenterChartVert = enumVertCentering.centertop;
    }
  }

  LoadNewSpacing(objTree) {
    this.fMarginMode = objTree.GetValueForChild("bmargmode", false);
    if (this.fMarginMode == null) {
      // Old File Format
      var aValue = objTree.GetValueForChild("spacingmode", false);
      if (aValue != null) this.fMarginMode = aValue;
      if (!MarginModeValid(aValue)) this.fMarginMode = kStandardMargins;
    }

    var mgrVSpacing1 = null;
    var valueList2 = objTree.GetValueListForChild("bmarg", true);
    if (valueList2) {
      this.fBMargins = new QRect();
      this.fBMargins.fLeft = valueList2[0];
      this.fBMargins.fTop = valueList2[1];
      this.fBMargins.fRight = valueList2[2];
      this.fBMargins.fBottom = valueList2[3];
    } else {
      // Old File Format
      var valueList3 = objTree.GetValueListForChild("boxspacing", true);
      if (valueList3) {
        this.fBMargins = new QRect();
        this.fBMargins.fLeft = valueList3[0];
        this.fBMargins.fTop = valueList3[1];
        this.fBMargins.fRight = valueList3[2];
        this.fBMargins.fBottom = valueList3[3];
        mgrVSpacing1 = valueList3[4];
      } else {
        if (this.fMarginMode == kManualMargins) {
          this.fBMargins = new QRect();
          this.fBMargins.InitRect(
            kHorzBoxSpacing,
            kVertBoxSpacing,
            kHorzBoxSpacing,
            kVertBoxSpacing
          );
        }
      }
    }

    var valueList4 = objTree.GetValueListForChild("bmgrspace", true);
    if (valueList4) {
      this.fMgrVSpacing1 = valueList4[0];
      this.fMgrVSpacing2 = valueList4[1];
    } else {
      this.fMgrVSpacing1 = mgrVSpacing1;
      this.fMgrVSpacing2 = null;
    }

    var valueList5 = objTree.GetValueListForChild("bspacing", true);
    if (valueList5) {
      this.fBSpacing = new QRect();
      this.fBSpacing.fLeft = valueList5[0];
      this.fBSpacing.fTop = valueList5[1];
      this.fBSpacing.fRight = valueList5[2];
      this.fBSpacing.fBottom = valueList5[3];
    }
  }

  LoadFromJSON(theLayout) {
    this.fBoxStyleID = theLayout["ID"];
    this.fBoxStyleName = theLayout["name"];
    this.fIsBoxStyle = this.fBoxStyleID != null && this.fBoxStyleName != null; //Only set for library entries
    this.fSet = theLayout["set"] || null;
    this.fBrandReactive = theLayout["brandReactive"] || false;
    var nodeData = theLayout["nodeData"];

    if (theLayout["libID"]) this.SetLibID(theLayout["libID"]);
    else if (!this.fIsBoxStyle) {
      //Load unique values that aren't fit to be saved to a shared library.
      var defaultSCFlag = enumSubChartFlag.nobreak;
      var mrbInfo = theLayout["container"];
      this.fObjID = nodeData["dbObjID"];
      this.fObjClassName = nodeData["className"];
      this.fBranchStyle = nodeData["branchStyle"];
      this.fBranchGroup = nodeData["branchGroup"];
      this.fSubChartFlag = nodeData["scFlag"] != null ? nodeData["scFlag"] : defaultSCFlag;
      var scID = 1;
      if (nodeData["scID"] != null) {
        scID = nodeData["scID"];
        if (isNaN(scID)) scID = null;
      }
      this.fSubChartID = scID;

      if (mrbInfo != null || (mrbInfo === undefined && nodeData["containerData"])) {
        var containerData = nodeData["containerData"];
        if (containerData["associatedObjs"]) {
          this.fAssociatedObjIDs = containerData["associatedObjs"];
        }
        if (containerData["containerObjIDRef"] != null) {
          this.SetContainerObjID(containerData["containerObjIDRef"]);
        }
        if (containerData["associatedChildren"]) {
          this.fAssociatedChildrenIDs = containerData["associatedChildren"];
        }
      }

      this.fLockFlag = nodeData["lock"];
      if (nodeData["boxMarginMode"]) this.fMarginMode = nodeData["boxMarginMode"];
      else this.fMarginMode = kStandardMargins;

      if (!MarginModeValid(this.fMarginMode)) this.fMarginMode = kStandardMargins;

      if (nodeData["margins"]) {
        this.fBMargins = new QRect();
        this.fBMargins.fLeft = nodeData["margins"]["left"];
        this.fBMargins.fTop = nodeData["margins"]["top"];
        this.fBMargins.fRight = nodeData["margins"]["right"];
        this.fBMargins.fBottom = nodeData["margins"]["bottom"];
      } else {
        if (this.fMarginMode == kManualMargins) {
          this.fBMargins = new QRect();
          this.fBMargins.InitRect(
            kHorzBoxSpacing,
            kVertBoxSpacing,
            kHorzBoxSpacing,
            kVertBoxSpacing
          );
        }
      }

      if (nodeData["mgrSpacing"]) {
        this.fMgrVSpacing1 = nodeData["mgrSpacing"]["1"];
        this.fMgrVSpacing2 = nodeData["mgrSpacing"]["2"];
      } else {
        this.fMgrVSpacing1 = null;
        this.fMgrVSpacing2 = null;
      }
      if (nodeData["spacing"]) {
        this.fBSpacing = new QRect();
        this.fBSpacing.fLeft = nodeData["spacing"]["left"];
        this.fBSpacing.fTop = nodeData["spacing"]["top"];
        this.fBSpacing.fRight = nodeData["spacing"]["right"];
        this.fBSpacing.fBottom = nodeData["spacing"]["bottom"];
      }

      if (nodeData["positioning"]) {
        var positioningInfo = nodeData["positioning"];
        this.fHorzPositioning = positioningInfo["horz"];
        this.fVertPositioning = positioningInfo["vert"];
        this.fDistRight = positioningInfo["right"];
        this.fDistBottom = positioningInfo["bottom"];
      }
      if (nodeData["centering"]) {
        this.fCenterChartHorz = nodeData["centering"]["horz"];
        this.fCenterChartVert = nodeData["centering"]["vert"];
      } else {
        this.fCenterChartVert = enumVertCentering.userplacement; //??not sure about this
        this.fCenterChartHorz = enumHorzCentering.userplacement;
      }
      var resetCenteringInfo = true;
      if (nodeData["userPosition"]) {
        if (
          this.fCenterChartHorz == enumHorzCentering.userplacement ||
          this.fCenterChartVert == enumVertCentering.userplacement
        ) {
          var userPosX = nodeData["userPosition"]["x"];
          var userPosY = nodeData["userPosition"]["y"];
          if (userPosX != "null" && userPosY != "null") {
            if (userPosX != "NaN" && userPosY != "NaN") {
              this.fUserPositionX = parseInt(userPosX);
              this.fUserPositionY = parseInt(userPosY);
              resetCenteringInfo = false;
            }
          }
        } else resetCenteringInfo = false;
      }

      if (resetCenteringInfo) {
        this.fCenterChartHorz = enumHorzCentering.centermiddle;
        this.fCenterChartVert = enumVertCentering.centertop;
      }

      if (nodeData["boxRefType"] != null) this.fBoxRefType = nodeData["boxRefType"];

      if (nodeData["libRefID"]) {
        this.SetLibRefID(nodeData["libRefID"]);
        return;
      }
    }

    this.fBoxType = theLayout["boxType"];
    this.fLayer = theLayout["layer"];

    this.fBoxSubType = theLayout["subType"];
    this.fBoxContentSubType = theLayout["boxContentSubType"];
    this.fBranchStyle = theLayout["branchStyle"];
    this.fBranchGroup = theLayout["branchGroup"];
    this.fHierarchyType = theLayout["hierarchyType"] ?? enumHierarchyTypes.employees;

    this.fContentAlign = theLayout["contentAlign"];

    this.fCenterChartHorz = enumHorzCentering.centermiddle;
    this.fCenterChartVert = enumVertCentering.centertop;

    if (theLayout["heightSettings"]) {
      this.fFixedHeight = theLayout["heightSettings"]["fixedHeight"];
      this.fHeightSizingMode = theLayout["heightSettings"]["mode"];
      this.fSmartHeight = -1;
    }

    if (theLayout["widthSettings"]) {
      this.fBoxWidth = theLayout["widthSettings"]["boxWidth"];
      this.fWidthSizingMode = theLayout["widthSettings"]["mode"];
      this.fWidthMin = theLayout["widthSettings"]["min"];
      this.fWidthMax = theLayout["widthSettings"]["max"];
      this.fUseCalcWidth = false;
    }

    if (theLayout["centering"]) {
      this.fCenterChartHorz = theLayout["centering"]["horz"];
      this.fCenterChartVert = theLayout["centering"]["vert"];
    }

    if (theLayout["positionInfo"]) {
      this.fNumCols = theLayout["positionInfo"]["numCols"];
      this.fNumRows = theLayout["positionInfo"]["numRows"];
      this.fPositionX = theLayout["positionInfo"]["xPos"];
      this.fPositionY = theLayout["positionInfo"]["yPos"];
    }

    if (theLayout["properties"]) {
      this.fBoxShapeType = theLayout["properties"]["shape"];
      this.fShowShadow = theLayout["properties"]["shadow"];
      this.fBoxFillColor = theLayout["properties"]["color"];
      this.fBoxFillMode = theLayout["properties"]["fillMode"];
      if (theLayout["properties"]["gradientColor"])
        this.fBoxFillColorGradient = theLayout["properties"]["gradientColor"];
    }

    if (theLayout["position"]) {
      this.fHorzPositioning = theLayout["position"]["xPos"];
      this.fVertPositioning = theLayout["position"]["yPos"];
      this.fDistRight = 0;
      this.fDistBottom = 0;
      if (theLayout["position"]["width"]) this.fDistRight = theLayout["position"]["width"];
      if (theLayout["position"]["height"]) this.fDistBottom = theLayout["position"]["height"];
    }

    if (theLayout["border"]) {
      this.fBorderThickness = theLayout["border"]["thickness"];
      this.fBorderStyle = theLayout["border"]["style"];
      this.fBoxBorderColor = theLayout["border"]["color"];
      this.fBoxPadding = theLayout["border"]["padding"];
      this.fDashStyle =
        theLayout["border"]["dash"] == null ? enumDashStyle.nodash : theLayout["border"]["dash"];
    }

    if (theLayout["boxFlag"]) {
      this.fFlagType = enumFlagType.triupperright;
      this.fFlagColor1 = theLayout["boxFlag"]["flagColor1"];
      this.flagColor2 = theLayout["boxFlag"]["flagColor2"];
      this.flagColor3 = theLayout["boxFlag"]["flagColor3"];
      this.fTriPosition = theLayout["boxFlag"]["triPosition"];
    }

    if (theLayout["container"]) {
      this.fContainerType = enumContainerType.standardmrb;
      this.fBoxSubType = enumBoxSubTypes.refMRBox; //TODO: This actually won't always be correct.
      this.fBoxType = enumBoxType.nonstandard; //TODO: This actually won't always be correct.
      this.fContainerNumCols = theLayout["container"]["numCols"];
      this.fContainerNumRows = theLayout["container"]["numRows"];
      this.fContainerTopLinesToColumns = theLayout["container"]["toplines"];
      this.fContainerBottomLinesToColumns = theLayout["container"]["bottom"];
      this.fContainerInternalLinesToColumns = theLayout["container"]["internal"];
    }

    this.fConnectingLineDesc = new QConnectingLineFormat();
    if (theLayout["connectingLine"])
      this.fConnectingLineDesc.LoadFromJSON(theLayout["connectingLine"]);

    if (theLayout["marginMode"]) this.fMarginMode = theLayout["marginMode"];
    else this.fMarginMode = "S";

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;

    this.fCellList = [];
    var cells = theLayout["cells"];
    for (var cellIdx in cells) {
      var theCell = cells[cellIdx];
      var newCell = new QBoxCellDesc();
      newCell.LoadFromJSON(theCell);
      newCell.SetGutterState(this.fNumCols, this.fNumRows);
      this.fCellList.push(newCell);
    }
    this.fCollapsed = false; //TODO, but this is not functional anyway
    this.fAssociatedObjIDs = this.fAssociatedObjIDs ?? [];

    this.fColWidths = theLayout["colWidths"];
    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;
  }

  SaveToJSON() {
    //TODO: Need to standardize names...
    var theLayout = {};
    var libID = this.GetLibID();
    if (libID != null) {
      theLayout["libID"] = libID;
    } else {
      //Everything in here ties the layout to a specific subchart node instance.
      var nodeData = {};
      theLayout["nodeData"] = nodeData;
      nodeData["dbObjID"] = this.fObjID;
      var scFlag = this.fSubChartFlag;
      var dbObj = this.GetDBObj();
      if (dbObj && dbObj["ignoreSCTop"] == true) scFlag = enumSubChartFlag.nobreak;
      nodeData["scID"] = this.fSubChartID;
      nodeData["scFlag"] = scFlag;
      nodeData["className"] = this.fObjClassName;
      nodeData["branchStyle"] = this.fBranchStyle;
      nodeData["branchGroup"] = this.fBranchGroup;

      if (this.fContainerType != enumContainerType.nonmrb) {
        nodeData["containerData"] = {};
        if (this.fAssociatedObjIDs != null && this.fAssociatedObjIDs.length > 0)
          nodeData["containerData"]["associatedObjs"] = this.fAssociatedObjIDs;

        if (this.GetAssociatedChildrenIDs())
          nodeData["containerData"]["associatedChildren"] = this.GetAssociatedChildrenIDs();
      }
      if (this.GetContainerObjID()) {
        if (!nodeData.hasOwnProperty("containerData")) nodeData["containerData"] = {};
        nodeData["containerData"]["containerObjIDRef"] = this.GetContainerObjID();
      }
      if (this.fLockFlag == 1) {
        nodeData["lock"] = this.fLockFlag == 1; //This is basically deprecated.
      }
      if (this.fMarginMode != kStandardMargins) nodeData["boxMarginMode"] = this.fMarginMode;

      if (this.fBMargins)
        nodeData["margins"] = {
          left: this.fBMargins.fLeft,
          top: this.fBMargins.fTop,
          right: this.fBMargins.fRight,
          bottom: this.fBMargins.fBottom
        };
      if (this.fMgrVSpacing1 != null && this.fMgrVSpacing2 != null) {
        var v1 = this.fMgrVSpacing1;
        if (this.fMgrVSpacing1 == null) v1 = 0;
        var v2 = this.fMgrVSpacing2;
        if (this.fMgrVSpacing2 == null) v2 = 0;
        nodeData["mgrSpacing"] = { 1: v1, 2: v2 };
      }

      if (this.fBSpacing)
        nodeData["spacing"] = {
          left: this.fBSpacing.fLeft,
          top: this.fBSpacing.fTop,
          right: this.fBSpacing.fRight,
          bottom: this.fBSpacing.fBottom
        };

      var defaultCC =
        this.fCenterChartHorz == enumHorzCentering.userplacement &&
        this.fCenterChartVert == enumVertCentering.userplacement;
      if (!defaultCC)
        //Is this duplicated?
        nodeData["centering"] = {
          horz: this.fCenterChartHorz,
          vert: this.fCenterChartVert
        };

      var defaultCP = IsDefaultCP(this);
      if (!defaultCP)
        nodeData["positioning"] = {
          horz: this.fHorzPositioning,
          vert: this.fVertPositioning,
          right: this.fDistRight,
          bottom: this.fDistBottom
        };

      if (
        this.fCenterChartHorz == enumHorzCentering.userplacement ||
        this.fCenterChartVert == enumVertCentering.userplacement
      ) {
        if (this.fPositionX != 0 || this.fPositionY != 0) {
          nodeData["userPosition"] = { x: this.fPositionX, y: this.fPositionY };
        }
      }
      if (this.fBoxRefType != null && this.fBoxRefType !== enumCellRefType.topbox)
        nodeData["boxRefType"] = this.fBoxRefType;

      // Load from Library
      var libRefID = this.GetLibRefID();
      if (libRefID != null) {
        nodeData["libRefID"] = libRefID;
        return theLayout;
      }
    }

    if (this.fIsBoxStyle) {
      theLayout["type"] = "boxstyle";
      //TODO: These are only from the libraries...
      theLayout["ID"] = this.fBoxStyleID;
      theLayout["name"] = this.fBoxStyleName;
    }

    theLayout["boxType"] = this.fBoxType;
    theLayout["layer"] = this.fLayer;

    theLayout["subType"] = this.fBoxSubType;
    theLayout["branchStyle"] = this.fBranchStyle;
    theLayout["branchGroup"] = this.fBranchGroup;
    theLayout["hierarchyType"] = this.fHierarchyType;
    theLayout["boxContentSubType"] = this.fBoxContentSubType;

    theLayout["contentAlign"] = this.fContentAlign;

    theLayout["brandReactive"] = this.fBrandReactive;

    var fixedHeight = -1;
    if (this.fHeightSizingMode != enumBoxHeightMode.autoself) fixedHeight = this.GetFixedHeight();

    var heightSettings = {};
    heightSettings["fixedHeight"] = fixedHeight;
    heightSettings["mode"] = this.fHeightSizingMode;
    theLayout["heightSettings"] = heightSettings;

    var widthSettings = {};
    widthSettings["boxWidth"] = this.fBoxWidth;
    widthSettings["mode"] = this.fWidthSizingMode;
    widthSettings["min"] = this.fWidthMin;
    widthSettings["max"] = this.fWidthMax;
    theLayout["widthSettings"] = widthSettings;

    var centering = {};
    centering["horz"] = this.fCenterChartHorz;
    centering["vert"] = this.fCenterChartVert;
    theLayout["centering"] = centering;

    var positionInfo = {};
    positionInfo["numCols"] = this.fNumCols;
    positionInfo["numRows"] = this.fNumRows;
    positionInfo["xPos"] = this.fPositionX;
    positionInfo["yPos"] = this.fPositionY;
    theLayout["positionInfo"] = positionInfo;

    var properties = {};
    properties["shape"] = this.fBoxShapeType;
    properties["shadow"] = this.fShowShadow;
    properties["color"] = this.fBoxFillColor;
    properties["fillMode"] = this.fBoxFillMode;
    properties["gradientColor"] = this.fBoxFillColorGradient;
    theLayout["properties"] = properties;

    var position = {};
    position["xPos"] = this.fHorzPositioning;
    position["yPos"] = this.fVertPositioning;
    position["width"] = this.fDistRight;
    position["height"] = this.fDistBottom;
    theLayout["position"] = position;

    var border = {};
    border["thickness"] = this.fBorderThickness;
    border["style"] = this.fBorderStyle;
    border["color"] = this.fBoxBorderColor;
    border["padding"] = this.fBoxPadding;
    border["dash"] = this.fDashStyle;
    theLayout["border"] = border;

    var connectingLine = {};
    connectingLine["color"] = this.fConnectingLineDesc.fLineColor;
    connectingLine["fillMode"] = this.fConnectingLineDesc.fFillMode;
    connectingLine["thickness"] = this.fConnectingLineDesc.fLineThickness;
    connectingLine["dash"] = this.fConnectingLineDesc.fDashStyle;
    theLayout["connectingLine"] = connectingLine;

    if (this.fFlagType !== enumFlagType.none) {
      const boxFlag = {};
      boxFlag.flagColor1 = this.fFlagColor1;
      boxFlag.flagColor2 = this.fFlagColor2;
      boxFlag.flagColor3 = this.fFlagColor3;
      boxFlag.triPosition = this.fTriPosition;

      theLayout["boxFlag"] = boxFlag;
    }

    if (this.fMarginMode != "S") theLayout["marginMode"] = this.fMarginMode;

    if (this.fContainerType != enumContainerType.nonmrb) {
      var container = {};
      container["numCols"] = this.fContainerNumCols;
      container["numRows"] = this.fContainerNumRows;
      container["toplines"] = this.fContainerTopLinesToColumns ? 1 : 0;
      container["bottom"] = this.fContainerBottomLinesToColumns ? 1 : 0;
      container["internal"] = this.fContainerInternalLinesToColumns ? 1 : 0;
      theLayout["container"] = container;
    }

    theLayout["colWidths"] = this.fColWidths;

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;

    var n = this.fCellList.length;
    theLayout["cells"] = [];
    //TODO: Why are we iterating twice? Proper list sorting should do the job.
    for (var i = 0; i < n; i++) {
      var cell = this.fCellList[i];
      if (cell.IsGutterCell()) {
        var json = this.fCellList[i].SaveToJSON();
        theLayout["cells"].push(json);
      }
    }

    for (var i = 0; i < n; i++) {
      var cell = this.fCellList[i];
      if (!cell.IsGutterCell()) {
        var json = this.fCellList[i].SaveToJSON();
        theLayout["cells"].push(json);
      }
    }

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;

    return theLayout;
  }

  LoadSubElements(objTree) {
    var version = 2;
    objTree.BuildIndex();

    var libID = objTree.GetValueForChild("libID", true);
    if (libID != null) this.SetLibID(libID);
    else {
      var objIDNode = objTree.GetChildByName("di");
      if (objIDNode == null) version = 1;

      var subChartInfo = null;
      var valueList0 = null;
      var valueList1 = null;
      var libRefID;
      var lockFlag = null;
      var cmGroup = null;
      var mrbInfo = null;

      if (version == 1) {
        this.fObjID = objTree.GetValueForChild("dbobjid", true);
        subChartInfo = objTree.GetValueListForChild("subchartinfo", true);
        this.fObjClassName = objTree.GetValueForChild("dbobjclassname", false);
        valueList0 = objTree.GetValueListForChild("branchstyle", true);
        valueList1 = objTree.GetValueListForChild("positioning", true);
        libRefID = objTree.GetValueForChild("libRefID", true);
        lockFlag = objTree.GetValueForChild("lockflag", true);
      } else {
        this.fObjID = objTree.GetValueForChild("di", true);
        subChartInfo = objTree.GetValueListForChild("si", true);
        this.fObjClassName = objTree.GetValueForChild("dc", false);
        valueList0 = objTree.GetValueListForChild("bs", true);
        valueList1 = objTree.GetValueListForChild("cp", true);
        libRefID = objTree.GetValueForChild("li", true);
        lockFlag = objTree.GetValueForChild("lf", true);

        //MRB Load
        mrbInfo = objTree.GetValueListForChild("mr", true);
        if (mrbInfo != null) {
          var valueListmrb = objTree.GetValueListForChild("ma", true);
          if (valueListmrb != null) {
            this.ClearAssociatedDBObjs();
            var n = valueListmrb.length;
            for (var i = 0; i < n; i++) {
              if (!isNaN(valueListmrb[i])) this.fAssociatedObjIDs.push(valueListmrb[i]);
            }
          }
        }
        var containerRef = objTree.GetValueForChild("ctnr", true);
        if (containerRef) this.SetContainerObjID(containerRef);
      }

      var subChartID = 1;
      var subChartFlag = enumSubChartFlag.nobreak;
      if (subChartInfo != null) {
        subChartID = subChartInfo[0];
        if (isNaN(subChartID)) subChartID = null;

        subChartFlag = subChartInfo[1];
      }
      this.fSubChartID = subChartID;
      this.fSubChartFlag = subChartFlag;
      this.fBranchStyle = valueList0[0];
      this.fBranchGroup = valueList0[1];

      if (mrbInfo != null) {
        this.fContainerType = enumContainerType.standardmrb;
        this.fBoxSubType = enumBoxSubTypes.refMRBox; //TODO: This actually won't always be correct.
        this.fBoxType = enumBoxType.nonstandard; //TODO: This actually won't always be correct.
        this.fContainerNumCols = mrbInfo[0];
        this.fContainerNumRows = mrbInfo[1];
        this.fContainerTopLinesToColumns = mrbInfo[2] == 1;
        this.fContainerBottomLinesToColumns = mrbInfo[3] == 1;
        this.fContainerInternalLinesToColumns = mrbInfo[4] == 1;
        var positionRefID = objTree.GetValueForChild("positionRefID", false);
        if (positionRefID != null) this.fPositionRefID = positionRefID;
      }

      if (lockFlag != null) this.fLockFlag = lockFlag;

      this.LoadNewSpacing(objTree);

      this.LoadCenteringInfo(objTree, version);
      if (valueList1) {
        this.fHorzPositioning = valueList1[0];
        this.fVertPositioning = valueList1[1];

        this.fDistRight = 0;
        this.fDistBottom = 0;
        if (valueList1.length > 2) {
          this.fDistRight = parseInt(valueList1[2]);
          this.fDistBottom = parseInt(valueList1[3]);
        }
      }

      if (libRefID != null) {
        this.SetLibRefID(libRefID);
        return;
      }
    }

    // This is only called if loading from the library
    var valueList = objTree.GetValueListForChild("boxwidth", true);
    this.fBoxWidth = valueList[0];
    this.fWidthSizingMode = valueList[1];
    this.fWidthMin = valueList[2];
    this.fWidthMax = valueList[3];
    this.fUseCalcWidth = false;

    valueList = objTree.GetValueListForChild("boxheight", true);
    if (valueList != null) {
      this.fFixedHeight = valueList[0];
      this.fHeightSizingMode = valueList[1];
      this.fSmartHeight = -1;
    }

    var contentAlign = objTree.GetValueForChild("contentalign", false);
    if (contentAlign == null) contentAlign = "Top";
    this.fContentAlign = contentAlign;

    valueList = objTree.GetValueListForChild("boxinfo", true);
    var boxType = enumBoxType.standard;
    var boxLayer = enumElementLayer.content;
    if (valueList != null) {
      boxType = valueList[0];
      boxLayer = valueList[1];
    }
    this.fBoxType = boxType;
    this.fLayer = boxLayer;

    this.fBoxSubType = objTree.GetValueForChild("boxsubtype", true);
    if (this.fBoxSubType == null) {
      this.fBoxSubType = enumBoxSubTypes.chartBox;
      if (boxType != enumBoxType.standard) this.fBoxSubType = enumBoxSubTypes.textBox;
    }

    var fieldName = objTree.GetValueForChild("fieldname", false);
    if (fieldName != null) {
      this.fFieldName = fieldName;
      this.fFieldID = objTree.GetValueForChild("fieldid", false);
      this.fBoxRefType = objTree.GetValueForChild("boxreftype", true);
      if (this.fBoxRefType == null) this.fBoxRefType = enumCellRefType.topbox;
    }

    valueList = objTree.GetValueListForChild("positioninfo", true);
    this.fNumCols = valueList[0];
    this.fNumRows = valueList[1];
    this.fPositionX = valueList[2];
    this.fPositionY = valueList[3];

    valueList = objTree.GetValueListForChild("boxprops", false);
    this.fBoxShapeType = parseInt(valueList[0]);
    this.fShowShadow = StringToBool(valueList[1]);
    this.fBoxFillColor = valueList[2];
    this.fBoxFillMode = parseInt(valueList[3]);

    valueList = objTree.GetValueListForChild("boxprops2", false);
    if (valueList) this.fBoxFillColorGradient = ConvertNull(valueList[0]);
    else this.fBoxFillColorGradient = "#FFFFFF";

    valueList = objTree.GetValueListForChild("linedash", true);
    if (valueList && valueList[0]) this.fConnectorDashStyle = valueList[0];
    if (valueList && valueList[1]) this.fConnectorWeightStyle = valueList[1];

    valueList = objTree.GetValueForChild("linecolor", false);
    if (valueList) this.fConnectorColor = valueList;

    valueList = objTree.GetValueListForChild("boxdash", false);
    if (valueList) this.fDashStyle = parseInt(valueList[0]);
    else this.fDashStyle = enumDashStyle.nodash;

    valueList = objTree.GetValueListForChild("borderprops", false);
    this.fBorderThickness = parseInt(valueList[0]);
    this.fBorderStyle = parseInt(valueList[1]);
    this.fBoxBorderColor = valueList[2];
    this.fBoxPadding = parseInt(valueList[3]);

    valueList = objTree.GetValueListForChild("annoflag", false);
    if (valueList) {
      this.fFlagType = parseInt(valueList[0]);
      this.fFlagLabel = ConvertNull(valueList[1]);
      this.fFlagColor1 = ConvertNull(valueList[2]);
      this.fFlagColor2 = ConvertNull(valueList[3]);
      this.fFlagColor3 = ConvertNull(valueList[4]);
    }

    var highlightInfo = objTree.GetValueForChild("highlight", false); // loading
    if (highlightInfo) {
      var highlightJSON = JSON.parse(highlightInfo);
      this.fHighlightInfo = {};
      this.fHighlightInfo = highlightJSON;
    }

    this.fTriPosition = objTree.GetValueForChild("tripos", true);
    if (this.fTriPosition == null) this.fTriPosition = 0;

    valueList = objTree.GetValueListForChild("colwidth", true);
    this.fColWidths = [];
    var n = valueList.length;
    for (var i = 0; i < n; i++) this.fColWidths.push(valueList[i]);

    /*
	valueList = objTree.GetValueListForChild("rowheight", true);
	this.fRowHeights = new Array();
	var n = valueList.length;
	for (var i=0;i<n;i++)
		this.fRowHeights.push(valueList[i]);
	*/

    this.fCollapsed = objTree.GetValueForChild("collapsed", true) == 1;

    var t = objTree.GetValueForChild("template", false);
    if (t != null) {
      this.fIsBoxStyle = true;
      this.fBoxStyleName = t;
      var tID = objTree.GetValueForChild("templateID", true);
      this.fBoxStyleID = tID;
    }
  }

  OutputChildElements(stream) {
    var refLibID = this.GetLibRefID();
    if (refLibID != null) return;

    this.fConnectingLineDesc.OutputToXML(stream);
    var n = this.fCellList.length;

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;
    //gPriorBorderSegment = null;

    for (var i = 0; i < n; i++) {
      var cell = this.fCellList[i];
      if (cell.IsGutterCell()) this.fCellList[i].OutputToXML(stream);
    }

    for (var i = 0; i < n; i++) {
      var cell = this.fCellList[i];
      if (!cell.IsGutterCell()) this.fCellList[i].OutputToXML(stream);
    }

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;
    //gPriorBorderSegment = null;
  }

  LoadChildElements(objTree) {
    var refLibID = this.GetLibRefID();
    if (refLibID != null) return;

    var clDesc = objTree.GetChildByName("connectingline");
    this.fConnectingLineDesc = new QConnectingLineFormat();
    this.fConnectingLineDesc.LoadFromXML(clDesc);

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;
    // gPriorBorderSegment = null;
    this.fCellList = [];

    var childList = objTree.GetChildOfTypeList("boxcell");
    var n = childList.length;
    for (var i = 0; i < n; i++) {
      var boxCellInfo = childList[i];
      var newCell = new QBoxCellDesc();
      newCell.LoadFromXML(boxCellInfo);
      newCell.SetGutterState(this.fNumCols, this.fNumRows);
      this.fCellList.push(newCell);
    }

    Globals._priorTextFormat = null;
    Globals._priorCellFormat = null;
    // gPriorBorderSegment = null;
  }

  //=================================================================================================
  // Update Properties
  RestoreProperties(srcLayout) {
    this.Journal();

    this.fPositionX = srcLayout.fPositionX;
    this.fPositionY = srcLayout.fPositionY;

    this.fBoxBorderColor = srcLayout.fBoxBorderColor;
    this.fShowShadow = srcLayout.fShowShadow;
    this.fConnectorDashStyle = srcLayout.fConnectorDashStyle;
    this.fConnectorWeightStyle = srcLayout.fConnectorWeightStyle;
    this.fConnectorColor = srcLayout.fConnectorColor;

    this.fBranchStyle = srcLayout.fBranchStyle; //??
    this.fBranchGroup = srcLayout.fBranchGroup;

    this.fBoxShapeType = srcLayout.fBoxShapeType;
    this.fBoxFillMode = srcLayout.fBoxFillMode;
    this.fBoxFillColor = srcLayout.fBoxFillColor;
    this.fBoxFillColorGradient = srcLayout.fBoxFillColorGradient;

    this.fBorderThickness = srcLayout.fBorderThickness;
    this.fBorderStyle = srcLayout.fBorderStyle;
    this.fDashStyle = srcLayout.fDashStyle;

    this.fFlagType = srcLayout.fFlagType;
    this.fFlagColor1 = srcLayout.fFlagColor1;
    this.fFlagColor2 = srcLayout.fFlagColor2;
    this.fFlagColor3 = srcLayout.fFlagColor3;
    this.fTriPosition = srcLayout.fTriPosition;

    this.fFieldName = srcLayout.fFieldName;
    this.fFieldID = srcLayout.fFieldID;
    this.fFieldRef = srcLayout.fFieldRef;
    this.fTableValues = srcLayout.fTableValues;

    this.fValueColorMap = srcLayout.fValueColorMap;

    this.fFlagLabel = srcLayout.fFlagLabel;

    this.fWidthSizingMode = srcLayout.fWidthSizingMode;
    this.fWidthMin = srcLayout.fWidthMin;
    this.fWidthMax = srcLayout.fWidthMax;

    this.fHighlightInfo = srcLayout.fHighlightInfo;
    this.fHeightSizingMode = srcLayout.fHeightSizingMode;
    this.fFixedHeight = srcLayout.fFixedHeight;
    this.fSmartHeight = -1;

    this.fContainerTopLinesToColumns = srcLayout.fContainerTopLinesToColumns;
    this.fContainerBottomLinesToColumns = srcLayout.fContainerBottomLinesToColumns;
    this.fContainerInternalLinesToColumns = srcLayout.fContainerInternalLinesToColumns;
    this.fContainerNumCols = srcLayout.fContainerNumCols;
    this.fContainerNumRows = srcLayout.fContainerNumRows;

    this.fContentAlign = srcLayout.fContentAlign;
    this.fBoxType = srcLayout.fBoxType;
    this.fBoxSubType = srcLayout.fBoxSubType;

    this.fUseCalcWidth = srcLayout.fUseCalcWidth;

    this.fLayer = srcLayout.fLayer;
    this.fBoxPadding = srcLayout.fBoxPadding;

    this.fNumCols = srcLayout.fNumCols;
    this.fNumRows = srcLayout.fNumRows;
    this.fBoxWidth = srcLayout.fBoxWidth;

    this.fCenterChartHorz = srcLayout.fCenterChartHorz;
    this.fCenterChartVert = srcLayout.fCenterChartVert;

    this.fHorzPositioning = srcLayout.fHorzPositioning;
    this.fVertPositioning = srcLayout.fVertPositioning;
    this.fDistRight = srcLayout.fDistRight;
    this.fDistBottom = srcLayout.fDistBottom;

    this.fAssociatedObjIDs =
      srcLayout.fAssociatedObjIDs != null
        ? [...srcLayout.fAssociatedObjIDs].filter(id => id != null)
        : null;

    this.fSubChartFlag = srcLayout.fSubChartFlag;
    this.fSubChartID = srcLayout.fSubChartID;

    this.fContainerObjID = srcLayout.fContainerObjID;
    this.fBoxRefType = srcLayout.fBoxRefType;

    this.fColWidths = [];
    var n = srcLayout.fColWidths.length;
    for (var i = 0; i < n; i++) this.fColWidths.push(srcLayout.fColWidths[i]);

    this.fConnectingLineDesc.RestoreProperties(srcLayout);

    this.fOverrides = srcLayout.fOverrides;
    this.unusedCellOverrides = srcLayout.unusedCellOverrides;

    this.fCellList = [];
    var n2 = srcLayout.fCellList.length;
    for (var i = 0; i < n2; i++) {
      var c = srcLayout.fCellList[i].Clone();
      this.fCellList.push(c);
      c.SetParentLayout(this);
    }
    //}
  }

  //=================================================================================================
  // Compare Properties
  CompareProperties(srcLayout, exactMatch, includeFreeText) {
    if (this.fBoxBorderColor != srcLayout.fBoxBorderColor) return "BoxBorderColor";

    if (this.fShowShadow != srcLayout.fShowShadow) return "BoxShadow";

    if (
      this.fConnectorDashStyle != srcLayout.fConnectorDashStyle ||
      this.fConnectorWeightStyle != srcLayout.fConnectorWeightStyle
    )
      return "DashStyle";

    if (this.fConnectorColor != srcLayout.fConnectorColor) return "LineColor";

    if (this.fBoxShapeType != srcLayout.fBoxShapeType) return "BoxShapeType";

    if (this.fBoxFillMode != srcLayout.fBoxFillMode) return "BoxFillMode";

    if (this.fBoxFillMode != enumFillMode.noFill) {
      if (this.fBoxFillColor != srcLayout.fBoxFillColor) return "BoxFillColor";

      if (this.fBoxFillColorGradient != srcLayout.fBoxFillColorGradient) return "BoxFillColor2";
    }

    if (this.fBorderThickness != srcLayout.fBorderThickness) return "BorderThickness";

    if (this.fBorderStyle != srcLayout.fBorderStyle) return "BorderStyle";

    if (this.fDashStyle != srcLayout.fDashStyle) return "BorderStyle";

    if (this.fBoxType != srcLayout.fBoxType) return "BoxType";

    if (this.fBoxSubType != srcLayout.fBoxSubType) return "BoxSubType";

    if (this.fFlagType != srcLayout.fFlagType) return "FlagType";

    if (this.fFlagColor1 != srcLayout.fFlagColor1) return "FlagColor1";

    if (this.fFlagColor2 != srcLayout.fFlagColor2) return "FlagColor2";

    if (this.fFlagColor3 != srcLayout.fFlagColor3) return "FlagColor3";

    if (this.fTriPosition != srcLayout.fTriPosition) return "TriPos";

    if (this.fFlagLabel != srcLayout.fFlagLabel) return "FlagLabel";

    if (exactMatch) {
      if (this.fHeightSizingMode != srcLayout.fHeightSizingMode) return "VSizingMode";

      if (this.fHeightSizingMode == enumBoxHeightMode.fixedheight) {
        if (this.fFixedHeight != srcLayout.fFixedHeight) return "FixedHeight";
      }

      if (this.fContentAlign != srcLayout.fContentAlign) return "ContentAlign";

      if (this.fWidthSizingMode != srcLayout.fWidthSizingMode) return "HSizingMode";

      if (this.fWidthSizingMode != enumBoxWidthMode.fixedwidth) {
        if (this.fWidthMin != srcLayout.fWidthMin) return "MinWidth";

        if (this.fWidthMax != srcLayout.fWidthMax) return "MaxWidth";
      } else {
        if (this.fBoxWidth != srcLayout.fBoxWidth) return "BoxWidth";
      }
    }

    if (this.fBoxPadding != srcLayout.fBoxPadding) return "BoxPadding";

    if (this.fConnectingLineDesc.CompareProperties(srcLayout.fConnectingLineDesc) == false)
      return "ConnectingLine";

    var checkWidth = this.fWidthSizingMode == enumBoxWidthMode.fixedwidth;

    if (this.fContainerType != srcLayout.fContainerType) return "MRBBoxStyle";

    if (this.fContainerType == enumContainerType.standardmrb) {
      if (this.fContainerTopLinesToColumns != srcLayout.fContainerTopLinesToColumns)
        return "MRBTopLines";
      if (this.fContainerBottomLinesToColumns != srcLayout.fContainerBottomLinesToColumns)
        return "MRBBottomLines";
      if (this.fContainerInternalLinesToColumns != srcLayout.fContainerInternalLinesToColumns)
        return "MRBInternalLines";
      if (this.fContainerNumRows != srcLayout.fContainerNumRows) return "MRBRows";
      if (this.fContainerNumCols != srcLayout.fContainerNumCols) return "MRBCols";
    }
    if (this.fFieldRef != srcLayout.fFieldRef) return "AggregatedField";

    if (this.fBoxRefType != srcLayout.fBoxRefType) return "RefType";

    if (this.fHighlightInfo) {
      var curHighlightInfo = JSON.stringify(this.fHighlightInfo); // save
      var curSrcHighlightInfo = JSON.stringify(srcLayout.fHighlightInfo);
      if (curHighlightInfo && curSrcHighlightInfo && curHighlightInfo != curSrcHighlightInfo)
        return "Highlight";
    }

    if (this.fCellList) {
      var n = this.fCellList.length;
      var sn = 0;
      if (srcLayout.fCellList) sn = srcLayout.fCellList.length;

      if (n != sn) return "cell";

      for (var i = 0; i < n; i++) {
        var newCell = this.fCellList[i];
        var prevCell = srcLayout.fCellList[i];

        var result = newCell.CompareProperties(
          prevCell,
          exactMatch,
          checkWidth,
          newCell.IsFreeText()
        );
        if (result == false) return "Cell";
      }
    }

    return "Match";
  }

  InitColumnWidths() {
    var numCols = this.fNumCols;
    var boxWidth = this.fBoxWidth;

    this.fColWidths = [];
    // Push the padding area before the first column
    this.fColWidths.push(this.fBoxPadding);

    // Push the columns, column 1 gets the remainder
    //var availableWidth = boxWidth - (2*kBoxPadding+(numCols+1));
    var availableWidth = boxWidth - (2 * this.fBoxPadding - 2);
    var colWidth = Math.floor(availableWidth / numCols);
    var leftOver = availableWidth - ColWidthSum(colWidth, numCols);
    colWidth = colWidth + Math.floor(leftOver / numCols);
    var remainder = availableWidth - ColWidthSum(colWidth, numCols);

    for (var i = 0; i < numCols; i++) {
      if (i == numCols - 1) this.fColWidths.push(colWidth + remainder);
      else this.fColWidths.push(colWidth);
    }

    // Push the padding area before the first column
    this.fColWidths.push(this.fBoxPadding);
  }

  SplitColumn(index) {
    var colWidth = this.fColWidths[index] + 1;
    var newWidth1 = Math.floor(colWidth / 2);
    var newWidth2 = colWidth - newWidth1;

    this.fColWidths[index] = newWidth1;
    this.fColWidths.splice(index, 0, newWidth2);
  }

  RemoveColumn(index) {
    var colWidth = this.fColWidths[index];
    var numColWidths = this.fColWidths.length;

    var mergeIndex = index + 1;
    if (index >= numColWidths - 2) mergeIndex = index - 1;

    this.fColWidths[mergeIndex] += colWidth - 1;

    this.fColWidths.splice(index, 1);
  }

  IncludesRow(aCell, rowIndex) {
    var y = aCell.GetRowIndex();
    if (rowIndex == y) return true;

    if (rowIndex >= y && rowIndex <= y + aCell.GetMergeY()) return true;
  }

  IncludesCol(aCell, colIndex) {
    var x = aCell.GetColIndex();
    if (colIndex == x) return true;

    if (colIndex >= x && colIndex <= x + aCell.GetMergeX()) return true;
  }

  FixMergedCells(rowIndex, colIndex) {
    let newCellList = [...this.fCellList];
    const numCells = this.fCellList.length;

    for (let i = 0; i < numCells; i++) {
      const aCell = this.fCellList[i];

      const isMerged = aCell.IsMergedCell();
      if (!isMerged) continue;

      const x = aCell.GetColIndex();
      const y = aCell.GetRowIndex();

      if (rowIndex != -1) {
        if (y === rowIndex) {
          if (aCell.GetMergeY() >= 1) {
            // If merged cell spans the row to be deleted, shift the cell down
            // so the contents of the cell are not lost
            newCellList = newCellList.filter(cell => {
              const cellX = cell.GetColIndex();
              const cellY = cell.GetRowIndex();
              return cellX !== x || cellY !== y + 1;
            });

            aCell.ShiftCellRow(false);
            aCell.SetMergeY(aCell.GetMergeY() - 1);
          }
        } else if (this.IncludesRow(aCell, rowIndex)) {
          aCell.SetMergeY(aCell.GetMergeY() - 1);
        }
      }

      if (colIndex != -1) {
        if (x === colIndex) {
          if (aCell.GetMergeX() >= 1) {
            newCellList = newCellList.filter(cell => {
              const cellX = cell.GetColIndex();
              const cellY = cell.GetRowIndex();
              return cellX !== x + 1 || cellY !== y;
            });

            aCell.ShiftCellColumn(false);
            aCell.SetMergeX(aCell.GetMergeX() - 1);
          }
        } else if (this.IncludesCol(aCell, colIndex)) {
          aCell.SetMergeX(aCell.GetMergeX() - 1);
        }
      }
    }

    this.fCellList = newCellList;
  }

  DeleteRow(index) {
    this.FixMergedCells(index, -1);
    var newCellList = [];
    while (this.fCellList.length > 0) {
      var aCell = this.fCellList.pop();
      var y = aCell.GetRowIndex();
      if (index != y) {
        if (y > index) aCell.ShiftCellRow(true);
        newCellList.push(aCell);
      }
    }

    this.fNumRows = this.fNumRows - 1;

    this.fCellList = newCellList;
  }

  DeleteColumn(index) {
    this.FixMergedCells(-1, index);
    var newCellList = [];
    while (this.fCellList.length > 0) {
      var aCell = this.fCellList.pop();
      var x = aCell.GetColIndex();
      if (index != x) {
        if (x > index) aCell.ShiftCellColumn(true);
        newCellList.push(aCell);
      }
    }

    this.fNumCols = this.fNumCols - 1;
    this.fCellList = newCellList;

    this.RemoveColumn(index);
  }

  AddCellRow(rowIndex, addRowHeight, insertAbove) {
    //if (addRowHeight)
    //	console.log("AddCellRow");

    var xCount = this.fNumCols + 2;
    var r = rowIndex;
    var cellsToPush = [];

    if (addRowHeight) {
      if (!insertAbove) r = rowIndex + 1;
    }

    for (var x = 0; x < xCount; x++) {
      var neighbor = null;
      if (addRowHeight) {
        var neighborRow;
        if (insertAbove) neighborRow = r + 1;
        else neighborRow = r - 1;
        neighbor = this.GetCellDesc(x, neighborRow);
      }

      var aCell = new QBoxCellDesc();
      aCell.InitBoxCell(x, r, this);
      if (neighbor != null) aCell.CopyCellFormat(neighbor);

      aCell.SetGutterState(this.fNumCols, this.fNumRows);
      cellsToPush.push(aCell);
    }
    // ... is the rest operator, it splits a list into a comma separated list of arguments
    this.fCellList.push(...cellsToPush);
    return cellsToPush;
  }

  AddCellColumn(colIndex, insertLeft) {
    var xCount = this.fNumCols + 2;
    var yCount = this.fNumRows + 2;
    var cellsToPush = [];

    var splitCol = colIndex;
    if (!insertLeft) splitCol = colIndex + 1;

    for (var y = 0; y < yCount; y++) {
      var aCell = new QBoxCellDesc();

      var neighborcol;
      if (insertLeft) neighborcol = colIndex + 1;
      else neighborcol = colIndex;

      var neighbor = this.GetCellDesc(neighborcol, y);
      aCell.InitBoxCell(splitCol, y, this);
      if (neighbor != null) aCell.CopyCellFormat(neighbor);

      aCell.SetGutterState(this.fNumCols, this.fNumRows);
      cellsToPush.push(aCell);
    }
    // ... is the rest operator, it splits a list into a comma separated list of arguments
    this.fCellList.push(...cellsToPush);
    this.SplitColumn(colIndex);
    return cellsToPush;
  }

  InsertRow(rowIndex, insertAbove) {
    var r = rowIndex + 1;
    if (insertAbove) r = rowIndex;

    var numCells = this.fCellList.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fCellList[i];
      var y = aCell.GetRowIndex();
      if (y >= r) aCell.ShiftCellRow(false);
    }

    this.fNumRows++;
    const newCells = this.AddCellRow(rowIndex, true, insertAbove);
    this.FixMergedCellsPostRowInsert(newCells, rowIndex);
    return newCells;
  }

  InsertColumn(colIndex, insertLeft) {
    var shiftStart = colIndex;
    if (!insertLeft) shiftStart = colIndex + 1;

    var numCells = this.fCellList.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fCellList[i];
      var x = aCell.GetColIndex();

      if (x >= shiftStart) aCell.ShiftCellColumn(false);
    }

    this.fNumCols++;
    const newCells = this.AddCellColumn(colIndex, insertLeft);
    this.FixMergedCellsPostColumnInsert(newCells, colIndex);
    return newCells;
  }

  FixMergedCellsPostRowInsert(newCells, rowIndex) {
    const containsNewCells = cell => {
      const y = cell.GetRowIndex();
      const mergeY = cell.GetMergeY();
      return mergeY > 0 && y <= rowIndex && y + mergeY >= rowIndex;
    };

    const intersectsWith = (oldCell, newCell) => {
      return oldCell.GetColIndex() === newCell.GetColIndex();
    };

    const extendMerge = (cell, hiddenCells) => {
      cell.SetMergeY(cell.GetMergeY() + hiddenCells);
    };

    this.FixMergedCellsPostInsert(newCells, containsNewCells, intersectsWith, extendMerge);
  }

  FixMergedCellsPostColumnInsert(newCells, colIndex) {
    const containsNewCells = cell => {
      const x = cell.GetColIndex();
      const mergeX = cell.GetMergeX();
      return mergeX > 0 && x <= colIndex && x + mergeX >= colIndex;
    };

    const intersectsWith = (oldCell, newCell) => {
      return oldCell.GetRowIndex() === newCell.GetRowIndex();
    };

    const extendMerge = (cell, hiddenCells) => {
      cell.SetMergeX(cell.GetMergeX() + hiddenCells);
    };

    this.FixMergedCellsPostInsert(newCells, containsNewCells, intersectsWith, extendMerge);
  }

  FixMergedCellsPostInsert(newCells, containsNewCells, intersectsWith, extendMerge) {
    const numCells = this.fCellList.length;
    for (let i = 0; i < numCells; i++) {
      const aCell = this.fCellList[i];

      if (!containsNewCells(aCell)) {
        continue;
      }

      let hiddenCells = 0;
      newCells.forEach(newCell => {
        if (!intersectsWith(aCell, newCell)) {
          return;
        }

        hiddenCells += 1;
        newCell.SetHidden(true);
        newCell.SetField(null);
      });

      extendMerge(aCell, hiddenCells);
    }
  }

  InitCellList() {
    this.fCellList = [];

    var yCount = this.fNumRows + 2;
    for (var y = 0; y < yCount; y++) this.AddCellRow(y, false, false);
  }

  CalcCellWidths() {
    var contentWidth = 0;
    var sizingMode = this.GetWidthSizingMode();
    if (sizingMode == enumBoxWidthMode.fixedwidth) return;

    if (sizingMode == enumBoxWidthMode.autosmart) if (!this.fUseCalcWidth) return;

    var numCells = this.fCellList.length;
    var lastCol = this.GetLastColIndex();
    var lastColWidth = this.GetLastColWidth();

    for (var n = 0; n < numCells; n++) {
      var aCell = this.fCellList[n];

      var curIndex = aCell.GetColIndex();
      if (lastCol == curIndex && !aCell.IsGutterCell()) {
        if (aCell.IsPicture()) contentWidth = aCell.GetPictureFrameWidth(true);
        else {
          var recalcContent = aCell.GetDisplayContent();
          if (recalcContent != null) {
            var txtFormat = aCell.GetTextFormat();
            var fontString = txtFormat.BuildFontString();
            var tw = MeasureText2(recalcContent, fontString, aCell.IsAutoText());
            tw = tw + this.GetRightInset(aCell, 4) + this.GetLeftInset(aCell, 4);
            contentWidth = Math.max(tw, contentWidth);
          }
        }
      }
    }

    contentWidth = contentWidth + 4;
    this.AutoResizeColumn(contentWidth, false);
  }

  GetRightInset(aCell, baseVal) {
    var annoFlag = this.GetFlagType();
    if (annoFlag == enumFlagType.none) return baseVal;
    if (this.GetTriPosition() === 1) return baseVal;

    var rIndex = aCell.GetRowIndex();
    if (rIndex != 1) return baseVal;

    var cIndex = aCell.GetColIndex();
    var nCols = this.GetNumCols();
    if (cIndex == nCols) {
      var delta = this.CalculateCellFlagOffset();
      return Math.max(delta, baseVal);
    }

    return baseVal;
  }

  GetLeftInset(aCell, baseVal) {
    var annoFlag = this.GetFlagType();
    if (annoFlag == enumFlagType.none) return baseVal;
    // only concerned with left triangles
    if (this.GetTriPosition() === 0) return baseVal;

    var rIndex = aCell.GetRowIndex();
    // only concerned with the topmost row
    if (rIndex != 1) return baseVal;
    //only concerned with the leftmost column
    var cIndex = aCell.GetColIndex();
    if (cIndex == 1) {
      var delta = this.CalculateCellFlagOffset();
      return Math.max(delta, baseVal);
    }

    return baseVal;
  }

  CalculateCellFlagOffset() {
    //TODO: Cache?
    var s = this.GetBoxPadding();
    //s = 0;
    //kTriAnnoWCalc
    var hypotenuseFlag = kTriAnnoWCalc;
    var hypotenuseCellMargin = Math.sqrt(2 * s * s);
    var delta = hypotenuseFlag - hypotenuseCellMargin;

    delta = Math.ceil(delta);
    return delta;
  }

  CalcCellHeight(aCell, rowHeights = []) {
    if (!this.IsMergedCell(aCell)) {
      return this.DoCalcCellHeight(aCell);
    }

    const mergeY = aCell.GetMergeY();
    let cellHeight = this.DoCalcCellHeight(aCell);
    for (let i = 1; i <= mergeY; i++) {
      const rowHeight = rowHeights[rowHeights.length - i] ?? 0;
      cellHeight -= rowHeight;
    }

    return cellHeight;
  }

  DoCalcCellHeight(aCell) {
    var pictureType = aCell.GetPictureType();
    switch (pictureType) {
      case enumPictureType.staticPicture:
        return aCell.GetCellHeight();
      case enumPictureType.fieldPicture:
      case enumPictureType.iconPicture:
        return aCell.GetPictureFrameHeight(true); // Padding
    }
    if (aCell.GetCellContentType() == enumCellContentType.pieChart) {
      aCell.SetCellHeight(aCell.GetCellWidth());
      return aCell.GetCellHeight();
    }
    if (aCell.GetCellContentType() === enumCellContentType.object) {
      var subType = aCell.GetCellObjectSubtype();
      if (subType == enumCellObjectSubtypes.alsoreportsto) {
        var calcHeight = aCell.GetARTCellHeight(aCell.GetDisplayContent());
        aCell.SetCellHeight(calcHeight);
        return aCell.GetCellHeight();
      }
    }

    var txtFormat = aCell.GetTextFormat();
    var fontString = txtFormat.BuildFontString();

    // For autosizing & text wrapping we need the actual content to do measurements
    var recalcContent = aCell.GetDisplayContent();
    if (recalcContent == null) {
      var measuredHeight = aCell.GetMeasuredHeight();
      if (measuredHeight == null) return kDefaultCellHeight;
      else return measuredHeight;
    }

    var cellHeight;
    var fillMode = aCell.GetCellFillMode();
    if (fillMode == enumFillMode.separator || aCell.IsGutterCell()) cellHeight = 1;
    else cellHeight = MeasureTextHeight(fontString, aCell.GetFontSize()) + 2;
    //var actualCellHeight = cellHeight;

    var numLines = 1;
    var actualLines = 1;
    var wrapText = aCell.GetWrapText();

    if (wrapText != enumWrapTextMode.nowrap) {
      var paddingL = this.GetLeftInset(aCell, 2);
      var paddingR = this.GetRightInset(aCell, 2);
      //	var x = aCell.GetColIndex();
      var colWidth = aCell.GetCellWidth(); // this.fColWidths[x];
      recalcContent = recalcContent.toString();
      var rows = BreakContent(
        recalcContent,
        aCell.GetMaxCellRows(),
        colWidth,
        paddingL,
        paddingR,
        fontString,
        aCell.GetEscBreakChars()
      );
      numLines = rows.length;
      actualLines = numLines;

      switch (wrapText) {
        case enumWrapTextMode.oneline:
          if (numLines > 1) numLines = 1;
          break;
        case enumWrapTextMode.twolines:
          if (numLines > 2) numLines = 2;
          break;
        case enumWrapTextMode.threelines:
          if (numLines > 3) numLines = 3;
          break;
        case enumWrapTextMode.fourlines:
          if (numLines > 4) numLines = 4;
          break;
      }
      //console.log("Col Width:", colWidth, " numlines:", numLines);
      //actualCellHeight = actualLines * cellHeight;
      cellHeight = numLines * cellHeight;
    }

    cellHeight = cellHeight + 2; // Add 1 pixel on the top and bottom

    aCell.SetCellNumRows(numLines, actualLines);
    aCell.SetMeasuredHeight(cellHeight);

    return cellHeight;
  }

  //=================================================================================================
  ContentYOffset() {
    var yPos = 0;

    var sizingMode = this.GetHeightSizingMode();
    if (sizingMode != enumBoxHeightMode.autoself) {
      var contentAlign = this.GetContentAlignment();
      var boxHeight = this.GetBoxHeight(true);
      var contentHeight = this.CalcBoxHeight();
      if (contentHeight < boxHeight) {
        var leftOver = boxHeight - contentHeight;
        switch (contentAlign) {
          case "Middle":
            yPos = Div2Floor(leftOver);
            break;
          case "Bottom":
            yPos = leftOver;
            break;
        }
      }
    }

    return yPos;
  }

  /** @return {boolean} */
  IsAutoSmartV() {
    var hSizingMode = this.GetHeightSizingMode();
    return hSizingMode === enumBoxHeightMode.autosmart;
  }

  IsAutoSmartH() {
    var wSizingMode = this.GetWidthSizingMode();
    if (wSizingMode == enumBoxWidthMode.autosmart) return true;

    return false;
  }

  GetAutoSmartHTag() {
    return this.fAutoSmartHFlag;
  }

  GetAutoSmartVTag() {
    return this.fAutoSmartVFlag;
  }

  TagAutoSmartH(setTag) {
    var tagged = false;
    if (setTag) tagged = this.IsAutoSmartH();

    this.fAutoSmartHFlag = tagged;
    return tagged;
  }

  /** @return {boolean} */
  TagAutoSmartV(setTag) {
    var tagged = this.IsAutoSmartV() && setTag;
    this.fAutoSmartVFlag = tagged;
    return tagged;
  }

  //=================================================================================================
  // Dynamic Sizing and Positioning
  // This is the magic sauce - run through the descriptors and calculate positioning
  CalculateLayout() {
    this.TagAutoSmartH(true);
    this.TagAutoSmartV(true);
    this.PCalculateLayout(false);
  }

  IsPicCell(aCell) {
    var isFieldPic = aCell.GetPictureType() == enumPictureType.fieldPicture;
    return isFieldPic;
  }

  IsMergedCell(aCell) {
    var isMergedCell = aCell.IsMergedCell();
    return isMergedCell;
  }

  CalcRowHeight(row, rowHeights) {
    if (row == 0 || row == this.fNumRows + 1) return this.fBoxPadding;

    var rowHeight = 0;
    var xCount = this.fNumCols + 2;
    for (var x = 0; x < xCount; x++) {
      var aCell = this.GetCellDesc(x, row);
      if (this.fExpandRow == null && aCell.GetExpandRow()) this.fExpandRow = row;
      var curCellHeight = this.CalcCellHeight(aCell, rowHeights);
      rowHeight = Math.max(rowHeight, curCellHeight);
    }

    return rowHeight;
  }

  ClearExpandRow() {
    var xCount = this.fNumCols + 2;
    var yCount = this.fNumRows + 2;
    for (var x = 0; x < xCount; x++) {
      for (var y = 0; y < yCount; y++) {
        var aCell = this.GetCellDesc(x, y);
        aCell.SetExpandRow(false);
        if (aCell.fOverrides.hasOwnProperty("fExpandRow")) {
          aCell.SetOverrideExpandRow(false);
        }
      }
    }
  }

  AdjustRowHeights(rowHeights, expandRow) {
    if (expandRow == null) return;

    const sizingMode = this.GetHeightSizingMode();
    const boxHeight = this.GetBoxHeight(false);
    if (sizingMode == enumBoxHeightMode.autoself) return;

    let leftOver;
    if (this.HasReservedHolderSpace()) {
      leftOver = this.GetHolderExpandSpace();
    } else {
      let currentHeight = 0;
      const l = rowHeights.length;
      for (let i = 0; i < l; i++) currentHeight += rowHeights[i];
      currentHeight = currentHeight - l + 1;
      leftOver = boxHeight - currentHeight;
    }

    if (leftOver > 0) rowHeights[expandRow] += leftOver;
  }

  //==============================================================================
  // Merged Cells
  CalcMerge(mCell) {
    var x = mCell.GetColIndex();
    var y = mCell.GetRowIndex();

    var mx = x + mCell.GetMergeX();
    var my = y + mCell.GetMergeY();

    var yNew = 0;
    var xNew;
    for (var z = y; z <= my; z++) {
      xNew = 0;
      var nextHeight = 0;
      for (var i = x; i <= mx; i++) {
        var nextCell = this.GetCellDesc(i, z);
        var nextWidth = nextCell.GetCellWidth();
        nextHeight = nextCell.GetCellHeight();
        //if (nextCell != mCell)
        //{
        //	nextCell.SetHidden(true);
        //	nextCell.SetField(null);
        //}
        xNew += nextWidth;
      }
      yNew += nextHeight;
    }

    xNew -= mx - x;
    mCell.SetCellWidth(xNew);

    yNew -= my - y;
    mCell.SetCellHeight(yNew);
  }

  HandleMergedCells(mergedCells) {
    var n = mergedCells.length;
    for (var i = 0; i < n; i++) {
      var mCell = mergedCells[i];
      this.CalcMerge(mCell);
    }
  }

  HideCellBlock(rootCell, left, top, right, bottom, hideIt) {
    for (var y = top; y <= bottom; y++) {
      for (var x = left; x <= right; x++) {
        var aCell = this.GetCellDesc(x, y);
        if (aCell != rootCell) {
          aCell.SetHidden(hideIt);
          aCell.SetField(null);
        }
      }
    }
  }

  DoMergeCells(cellList) {
    var n = cellList.length;
    if (n < 2) return;

    // Check legal - no cells can already be merged
    // MERGECELLZ

    var left = 1000;
    var right = 0;
    var top = 1000;
    var bottom = 0;

    for (var i = 0; i < n; i++) {
      var aCell = cellList[i];
      var x = aCell.GetColIndex();
      var y = aCell.GetRowIndex();
      left = Math.min(x, left);
      right = Math.max(x, right);
      top = Math.min(y, top);
      bottom = Math.max(y, bottom);
    }

    var mRoot = this.GetCellDesc(left, top);
    var mergeX = right - left;
    var mergeY = bottom - top;
    mRoot.SetMergeX(mergeX);
    mRoot.SetMergeY(mergeY);
    this.HideCellBlock(mRoot, left, top, right, bottom, true);

    return mRoot;
  }

  DoUnmergeCells(cellList) {
    var n = cellList.length;
    if (n == 0) return;

    var mList = new Array();
    for (var i = 0; i < n; i++) {
      var mCell = cellList[i];
      if (mCell.IsMergedCell()) {
        var mergeX = mCell.GetMergeX();
        var mergeY = mCell.GetMergeY();
        mCell.SetMergeX(0);
        mCell.SetMergeY(0);
        var x = mCell.GetColIndex();
        var y = mCell.GetRowIndex();
        this.HideCellBlock(mCell, x, y, x + mergeX, y + mergeY, false);
      }
    }
  }

  //==============================================================================
  PCalculateLayout(verbose) {
    this.CalcCellWidths();

    var boxHeight = 0;
    var xCount = this.fNumCols + 2;
    var yCount = this.fNumRows + 2;

    var maxHeight = 0;
    var xPos = 0;
    var yStart = this.ContentYOffset();

    if (verbose) console.log("PCalculateLayout - Details");

    var rowHeights = [];
    this.fExpandRow = null;
    for (var y = yCount - 1; y >= 0; y--) {
      var rowH = this.CalcRowHeight(y, rowHeights);
      rowHeights.push(rowH);
    }
    rowHeights.reverse();
    this.AdjustRowHeights(rowHeights, this.fExpandRow);

    var mergedCells = [];
    for (var x = 0; x < xCount; x++) {
      var yPos = yStart;

      var cellW = this.fColWidths[x];

      for (var y = 0; y < yCount; y++) {
        var aCell = this.GetCellDesc(x, y);
        aCell.SetLocation(xPos, yPos);
        if (this.IsMergedCell(aCell)) mergedCells.push(aCell);

        const cellH = rowHeights[y];

        aCell.SetCellHeight(cellH);
        aCell.SetCellWidth(cellW);

        yPos = yPos + cellH - 1;

        if (verbose)
          console.log(
            "Cell [",
            x,
            "][",
            y,
            "] W:",
            cellW,
            " H:",
            cellH,
            " cx:",
            xPos,
            " cy:",
            yPos
          );
      }

      xPos = xPos + this.fColWidths[x] - 1;
    }

    this.HandleMergedCells(mergedCells);
  }

  SumColWidths(colIndex) {
    var theSum = 0;
    for (var i = 0; i <= colIndex; i++) {
      theSum = theSum + this.fColWidths[i]; //  + 1;
      if (i > 0) theSum = theSum - 1;
    }

    return theSum;
  }

  AutoResizeColumn(newColWidth, calcLayout) {
    var colIndex = this.GetLastColIndex();
    //var colIndex = numColWidths - 2;
    var curWidth = this.fColWidths[colIndex];
    var delta = newColWidth - curWidth;

    var newBoxWidth = this.fBoxWidth + delta;

    //	var priorCols = this.SumColWidths(colIndex-1);

    var padding = this.GetBoxPadding();
    var minWidth = this.GetWidthMin(); // + padding;
    var maxWidth = this.GetWidthMax(); // + padding;

    if (newBoxWidth < minWidth) delta = delta + (minWidth - newBoxWidth);
    else if (newBoxWidth > maxWidth) delta = delta - (newBoxWidth - maxWidth);

    newBoxWidth = this.fBoxWidth + delta;
    if (this.fBoxWidth != newBoxWidth) {
      this.SetBoxWidthField(newBoxWidth);
      this.fColWidths[colIndex] = curWidth + delta;
      if (calcLayout) this.CalculateLayout();
    }
  }

  ResizeColumn(colIndex, delta, recalcLayout) {
    var curWidth = this.fColWidths[colIndex];
    this.fColWidths[colIndex] = curWidth + delta;

    var lastColIndex = this.GetLastColIndex();
    if (colIndex == lastColIndex) {
      var newWidth = this.fBoxWidth + delta;
      this.SetBoxWidthField(newWidth);
    } else {
      curWidth = this.fColWidths[colIndex + 1];
      this.fColWidths[colIndex + 1] = curWidth - delta;
    }

    if (recalcLayout) this.CalculateLayout();
  }

  //=================================================================================================
  // Get/Set Methods
  GetPositionX() {
    if (this.fPositionX == null) console.log("Opps");

    return this.fPositionX;
  }

  GetPositionY() {
    if (this.fPositionY == null) console.log("Opps");

    return this.fPositionY;
  }

  SetPosition(x, y) {
    if (x > 32000 || x < -32000) console.log("Problem!");

    // console.log("SetPosition",x,y);
    this.Journal();
    this.fPositionX = x;
    this.fPositionY = y;
  }

  GetBoxWidth() {
    return this.fBoxWidth;
  }

  SetBoxWidthField(boxWidth) {
    this.Journal();
    this.fBoxWidth = boxWidth;
  }

  GetNumRows() {
    return this.fNumRows;
  }

  GetNumCols() {
    return this.fNumCols;
  }

  GetNumColWidths() {
    return this.fColWidths.length;
  }

  GetColWidth(colIndex) {
    return this.fColWidths[colIndex];
  }

  HasReservedHolderSpace() {
    return (
      this.GetContainerType() == enumContainerType.standardmrb &&
      this.GetAssociatedObjIDs()?.length > 0 &&
      !!this.GetContainerEnvelope()
    );
  }

  GetHolderExpandSpace() {
    if (this.fExpandRow == null) return 0;
    if (this.GetHeightSizingMode() === enumBoxHeightMode.autoself) return 0;
    if (!this.HasReservedHolderSpace()) return 0;
    return Math.max(0, this.GetBoxHeight(false) - this.GetContainerEnvelope().y);
  }

  CalcBoxHeight() {
    var boxHeight = this.CalcContentHeight();

    //TODO: We still have an issue with the fixed sizing mode- won't work until we fix this flow.
    if (this.HasReservedHolderSpace()) boxHeight = this.GetContainerEnvelope().y;
    else if (this.GetContainerType() == enumContainerType.standardmrb)
      boxHeight += kDefaultCellHeight;

    return boxHeight;
  }

  CalcContentHeight() {
    const yCount = this.fNumRows + 2;

    const rowHeights = [];
    for (let y = yCount - 1; y >= 0; y--) {
      const rowH = this.CalcRowHeight(y, rowHeights);
      rowHeights.push(rowH);
    }

    return rowHeights.reduce((acc, cur) => acc + cur - 1, 1);
  }

  SetUseCalcWidth(useCalcWidth) {
    this.fUseCalcWidth = useCalcWidth;
  }

  GetUseCalcWidth() {
    return this.fUseCalcWidth;
  }

  GetBoxHeight(doRecalc) {
    if (!doRecalc && this.fCachedHeight != null) return this.fCachedHeight;

    var sizingMode = this.GetHeightSizingMode();
    var boxHeight = 0;

    switch (sizingMode) {
      case enumBoxHeightMode.fixedheight:
        boxHeight = this.GetFixedHeight();
        break;
      case enumBoxHeightMode.autoself:
        boxHeight = this.CalcBoxHeight();
        break;
      case enumBoxHeightMode.autosmart:
        var branchStyle = this.GetBranchStyle();
        if (IsPureVerticalStyle(branchStyle)) boxHeight = this.CalcBoxHeight();
        else boxHeight = this.GetSmartHeight();
        break;
    }
    this.fCachedHeight = boxHeight;
    return boxHeight;
  }

  GetBoxHeightForStyle(branchStyle) {
    var sizingMode = this.GetHeightSizingMode();
    var boxHeight = 0;

    switch (sizingMode) {
      case enumBoxHeightMode.fixedheight:
        boxHeight = this.GetFixedHeight();
        break;
      case enumBoxHeightMode.autoself:
        boxHeight = this.CalcBoxHeight();
        break;
      case enumBoxHeightMode.autosmart:
        if (IsPureVerticalStyle(branchStyle)) boxHeight = this.CalcBoxHeight();
        else boxHeight = this.GetSmartHeight();
        break;
    }

    return boxHeight;
  }

  GetNumCells() {
    return this.fCellList.length;
  }

  HasPhotoCell() {
    return this.fCellList.some(cell => cell.GetFieldSaveID() === kPhotoID);
  }

  GetIndexCellDesc(index) {
    return this.fCellList[index];
  }

  GetCellDesc(colIndex, rowIndex) {
    var numCells = this.fCellList.length;
    for (var i = 0; i < numCells; i++) {
      var aCell = this.fCellList[i];
      var x = aCell.GetColIndex();
      var y = aCell.GetRowIndex();
      if (x == colIndex && y == rowIndex) return aCell;
    }

    return null;
  }

  IsEmptyCell(colIndex, rowIndex) {
    const aCell = this.GetCellDesc(colIndex, rowIndex);
    if (!aCell || aCell.IsGutterCell()) {
      return false;
    }

    const contentType = aCell.GetCellContentType();

    switch (contentType) {
      case enumCellContentType.Blank:
        return true;
      case enumCellContentType.FreeText:
        const freeText = aCell.GetFreeText();
        return !freeText;
    }

    return false;
  }

  IsAggObj() {
    var boxType = this.GetBoxSubType();
    var isAggField = this.GetAggregatedFieldRef() != null;
    var obj = boxType === enumBoxSubTypes.enumeratedTable || boxType === enumBoxSubTypes.refChart;
    return obj && isAggField;
  }

  IsLayoutCellRefType() {
    var subType = this.GetBoxSubType();
    return subType === enumBoxSubTypes.enumeratedTable || subType === enumBoxSubTypes.refChart;
  }

  UpdateOverride(parameter, property, doOverride) {
    var boxProperty = true;
    var reBuildLayout = false;
    this.fOverrides = this.fOverrides || {};
    switch (property) {
      case enumUpdateProperty.boxFillColor:
        var curMode = this.GetBoxFillMode();
        var curColor = this.GetBoxFillColor();
        doOverride
          ? this.SetOverrideBoxFillMode(parameter.fFillMode)
          : this.SetBoxFillMode(parameter.fFillMode);
        if (parameter.fFillMode != enumFillMode.noFill) {
          doOverride
            ? this.SetOverrideBoxFillColor(parameter.fSelectedColor)
            : this.SetBoxFillColor(parameter.fSelectedColor);
          doOverride
            ? this.SetOverrideBoxFillColorGradient(parameter.fSelectedColor2)
            : this.SetBoxFillColorGradient(parameter.fSelectedColor2);
        }
        if (curMode != parameter.fFillMode || curColor != parameter.fSelectedColor) {
          var bs = this.GetBorderStyle();
          if (bs == enumElementBorderStyle.noBorder) reBuildLayout = true;
        }
        break;
      case enumUpdateProperty.boxBorderColor:
        doOverride
          ? this.SetOverrideBoxBorderColor(parameter.fSelectedColor)
          : this.SetBoxBorderColor(parameter.fSelectedColor);
        reBuildLayout = true; // picture mask
        break;
      case enumUpdateProperty.boxShapeType:
        doOverride ? this.SetOverrideBoxShapeType(parameter) : this.SetBoxShapeType(parameter);
        break;
      case enumUpdateProperty.boxShadow:
        doOverride ? this.SetOverrideShadowOn(parameter) : this.SetShadowOn(parameter);
        break;
      case enumUpdateProperty.boxBorderWeight:
        doOverride
          ? this.SetOverrideBorderThickness(parameter)
          : this.SetBorderThickness(parameter);
        break;
      case enumUpdateProperty.boxBorderStyle:
        doOverride ? this.SetOverrideBorderStyle(parameter) : this.SetBorderStyle(parameter);
        break;
      case enumUpdateProperty.boxDashStyle:
        doOverride ? this.SetOverrideDashStyle(parameter) : this.SetDashStyle(parameter);
        break;
      case enumUpdateProperty.boxPadding:
        this.SetBoxPadding(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.boxWidth:
        this.ChangeBoxWidth(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.boxHeight:
        this.ChangeBoxHeight(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.boxMinWidth:
        this.SetWidthMin(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.boxMaxWidth:
        this.SetWidthMax(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.contentAlign:
        this.SetContentAlignment(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.cellRefType:
        boxProperty = this.IsLayoutCellRefType();
        if (boxProperty) {
          this.SetCellRefType(parameter);
          reBuildLayout = true;
        }
        break;
      case enumUpdateProperty.mgrVSpacing1:
        doOverride ? this.SetOverrideMgrVSpacing1(parameter) : this.SetMgrVSpacing1(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.mgrVSpacing2:
        doOverride ? this.SetOverrideMgrVSpacing2(parameter) : this.SetMgrVSpacing2(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.resetspacing:
        reBuildLayout = true;
        break;
      case enumUpdateProperty.spacingMode:
        doOverride ? this.SetOverrideMarginMode(parameter) : this.SetMarginMode(parameter);
        reBuildLayout = true;
        break;
      case enumUpdateProperty.boxFlag:
        if (doOverride) {
          this.SetOverrideFlagType(parameter.fFlagType);
          this.SetOverrideFlagColor1(parameter.fFlagColor1);
          this.SetOverrideFlagColor2(parameter.fFlagColor2);
          this.SetOverrideFlagColor3(parameter.fFlagColor3);
          this.SetOverrideFlagLabel(parameter.fFlagLabel);
          this.SetOverrideTriPosition(parameter.fTriPosition);
        } else {
          this.SetFlagType(parameter.fFlagType);
          this.SetFlagColor1(parameter.fFlagColor1);
          this.SetFlagColor2(parameter.fFlagColor2);
          this.SetFlagColor3(parameter.fFlagColor3);
          this.SetFlagLabel(parameter.fFlagLabel);
          this.SetTriPosition(parameter.fTriPosition);
        }
        reBuildLayout = true;
        break;
      default:
        boxProperty = false;
        console.log("invalid command for box override: " + property);
        break;
    }

    return [boxProperty, reBuildLayout];
  }

  /*
***
Box Layout Styling
***
*/
  SetBoxBorderColor(borderColor) {
    this.Journal();
    this.fBoxBorderColor = borderColor;
  }

  SetOverrideBoxBorderColor(borderColor) {
    this.Journal();
    this.fOverrides["borderColor"] = borderColor;
  }

  GetBoxBorderColor() {
    return this.GetOverridesKey("borderColor", this.fBoxBorderColor);
  }

  // Shadow On
  SetShadowOn(shadowOn) {
    this.Journal();
    this.fShowShadow = shadowOn;
  }

  SetOverrideShadowOn(shadowOn) {
    this.Journal();
    this.fOverrides["shadowOn"] = shadowOn;
  }

  GetShadowOn() {
    return this.GetOverridesKey("shadowOn", this.fShowShadow);
  }

  GetLastColIndex() {
    var lastColIndex = this.GetNumColWidths() - 2;
    return lastColIndex;
  }

  GetLastColWidth() {
    var lastColIndex = this.GetLastColIndex();
    return this.fColWidths[lastColIndex];
  }

  GetMinWidthForBumpFirstCol() {
    var lastCol = 1;
    return this.SumColWidths(lastCol - 1) + kMinLastColWidth;
  }

  GetMinWidthForBump() {
    var lastCol = this.GetLastColIndex();
    return this.SumColWidths(lastCol - 1) + kMinLastColWidth;
  }

  SetBoxWidth(newWidth) {
    var curWidth = this.GetBoxWidth();
    var deltaX = newWidth - curWidth;
    this.ChangeBoxWidth(deltaX);
  }

  ChangeBoxWidth(delta) {
    var lastColIndex = this.GetLastColIndex();
    var lastColWidth = this.fColWidths[lastColIndex];
    var newLastColWidth = lastColWidth + delta;

    var actualDelta = delta;
    if (newLastColWidth < kMinLastColWidth) {
      actualDelta = -(lastColWidth - kMinLastColWidth);
      newLastColWidth = kMinLastColWidth;
    }

    this.fColWidths[lastColIndex] = newLastColWidth;
    var newWidth = this.fBoxWidth + actualDelta;
    this.SetBoxWidthField(newWidth);

    return actualDelta;
  } // 9.99 inches
  ChangeBoxHeight(delta) {
    var actualDelta = delta;
    var newHeight = this.fFixedHeight + delta;
    if (newHeight < kMinBoxHeight) actualDelta = kMinBoxHeight - this.fFixedHeight;
    else {
      var isPicture = this.GetBoxSubType() == enumBoxSubTypes.picture;
      if (!isPicture && newHeight > kMaxBoxHeight) actualDelta = kMaxBoxHeight - this.fFixedHeight;
    }

    this.fFixedHeight += actualDelta;

    return actualDelta;
  }

  SetBoxPadding(boxPadding) {
    var curPadding = this.GetBoxPadding();

    this.fBoxPadding = boxPadding;
    var lastColIndex = this.GetLastColIndex();
    this.fColWidths[0] = boxPadding;
    this.fColWidths[lastColIndex + 1] = boxPadding;

    // Box must remain the same width!
    var deltaPadding = curPadding - boxPadding;
    this.fColWidths[1] += deltaPadding;
    this.fColWidths[lastColIndex] += deltaPadding;

    //	var numR = this.fRowHeights.length;
    //	this.fRowHeights[0] = boxPadding;
    //	this.fRowHeights[numR-1] = boxPadding;
  }

  GetBoxPadding() {
    return this.fBoxPadding;
  }

  SetBranchStyle(branchStyle, doOverride) {
    if (doOverride) {
      this.SetOverrideBranchStyle(branchStyle);
      return;
    }
    this.Journal();
    var isAssist = IsAssistantStyle(branchStyle);
    this.SetBranchGroup(isAssist ?? 0);
    this.fBranchStyle = branchStyle;
  }

  SetOverrideBranchStyle(branchStyle) {
    this.Journal();
    var isAssist = IsAssistantStyle(branchStyle);
    this.SetOverrideBranchGroup(isAssist ?? 0);
    this.fOverrides["bstyle"] = branchStyle;
  }

  SetOverrideBranchGroup(bGroup) {
    this.fOverrides["bgroup"] = bGroup;
  }

  GetBranchStyle() {
    return this.GetOverridesKey("bstyle", this.fBranchStyle);
  }

  TransferBranchStyle(srcLayout) {
    if (srcLayout != null) {
      this.fBranchStyle = srcLayout.fBranchStyle;
      this.fBranchGroup = srcLayout.fBranchGroup;
    }
  }

  TransferMarginsAndSpacing(srcLayout) {
    if (srcLayout != null) {
      this.fMarginMode = srcLayout.fMarginMode;
      if (this.fMarginMode == kManualMargins) {
        if (this.fBMargins == null) this.fBMargins = new QRect();
        this.fBMargins.fLeft = srcLayout.fBMargins.fLeft;
        this.fBMargins.fRight = srcLayout.fBMargins.fRight;
        this.fBMargins.fTop = srcLayout.fBMargins.fTop;
        this.fBMargins.fBottom = srcLayout.fBMargins.fBottom;
      } else this.fBMargins = null;

      this.fMgrVSpacing1 = srcLayout.fMgrVSpacing1;
      this.fMgrVSpacing2 = srcLayout.fMgrVSpacing2;

      if (srcLayout.fBSpacing != null) {
        if (this.fBSpacing == null) this.fBSpacing = new QRect();

        this.fBSpacing.fLeft = srcLayout.fBSpacing.fLeft;
        this.fBSpacing.fRight = srcLayout.fBSpacing.fRight;
        this.fBSpacing.fTop = srcLayout.fBSpacing.fTop;
        this.fBSpacing.fBottom = srcLayout.fBSpacing.fBottom;
      } else this.fBSpacing = null;
    }
  }

  TransferHighlightProps(srcLayout) {
    if (srcLayout !== null) {
      var srcHighlightInfo = srcLayout.fHighlightInfo;

      this.fHighlightInfo = srcHighlightInfo ? { dynamic: srcHighlightInfo["dynamic"] } : null;
    }
  }

  HasOverrides() {
    var layoutOverrides = this.fOverrides;

    var hasLayoutOverrides = (layoutOverrides && Object.keys(layoutOverrides).length > 0) || false;
    if (hasLayoutOverrides) return true;

    let hasCachedOverrides = this.unusedCellOverrides != null;
    if (hasCachedOverrides) return true;

    var hasCellOverrides = this.fCellList.some(cell => cell.HasOverrides());
    return hasCellOverrides;
  }

  ValidateFreeTextOverrides(newOverrideDict, previousOverrideDict) {
    const newDict = {};
    for (const key in previousOverrideDict) {
      if (newOverrideDict[key] != null && newOverrideDict[key] == previousOverrideDict[key]) {
        newDict[key] = newOverrideDict[key];
      } else if (newOverrideDict[key] !== previousOverrideDict[key]) {
        newDict[key] = newOverrideDict[key];
      } else {
        newDict[key] = previousOverrideDict[key];
      }
    }

    return newDict;
  }

  TransferOverrides(srcLayout) {
    //TODO: look at this during unpacking
    if (!srcLayout?.HasOverrides()) return;

    const getUniqueIndexCell = cell => `${cell.fCol} - ${cell.fRow}`;

    // Cell Overrides, cell, textFormat and Free Text level
    const srcCellOverrideDict = {};
    const srcCellTextFormatOverrideDict = {};
    const srcCellFreeTextsOverrideDict = { ...(srcLayout.unusedCellOverrides?.freeText || {}) };

    // Copy layout overrides
    this.fOverrides = srcLayout.fOverrides;

    for (const cell of srcLayout.fCellList.filter(cell => !cell.IsGutterCell())) {
      //check if overrides exist
      //check alignment
      let hasCellOverrides = cell.fOverrides && Object.keys(cell.fOverrides).length > 0;
      let hasFormattingOverrides = Object.keys(cell.fTextFormat.fOverrides).length > 0;
      let hasFreeText = cell.GetFreeText();

      if (hasCellOverrides || hasFormattingOverrides || hasFreeText) {
        let fieldIdentifier = cell.fFieldName ?? getUniqueIndexCell(cell);
        if (hasCellOverrides) srcCellOverrideDict[fieldIdentifier] = cell.fOverrides;
        if (hasFormattingOverrides)
          srcCellTextFormatOverrideDict[fieldIdentifier] = cell.fTextFormat.fOverrides;
        if (hasFreeText) srcCellFreeTextsOverrideDict[fieldIdentifier] = cell.GetFreeText();
      }
    }
    let verifiedFreeTextsOverrideDict = this.ValidateFreeTextOverrides(
      srcCellFreeTextsOverrideDict,
      srcLayout.unusedCellOverrides?.freeText || {}
    );

    let hasCachedOverrides = srcLayout.unusedCellOverrides != null;
    if (hasCachedOverrides) {
      Object.assign(srcCellOverrideDict, srcLayout.unusedCellOverrides.cell);
      Object.assign(srcCellTextFormatOverrideDict, srcLayout.unusedCellOverrides.formatting);
      Object.assign(srcCellFreeTextsOverrideDict, verifiedFreeTextsOverrideDict);
    }

    const targetCells = this.fCellList.filter(x => x.fFieldName || x.IsFreeText());
    for (const cell of targetCells) {
      const targetFieldName = cell.fFieldName ?? getUniqueIndexCell(cell);
      if (targetFieldName) {
        cell.fOverrides = srcCellOverrideDict[targetFieldName] || cell.fOverrides;
        cell.fTextFormat.fOverrides =
          srcCellTextFormatOverrideDict[targetFieldName] || cell.fTextFormat.fOverrides;
      }

      if (cell.IsFreeText()) cell.SetFreeText(srcCellFreeTextsOverrideDict[targetFieldName]);

      //Delete overrides that were applied so that we can only cache unused ones.
      delete srcCellOverrideDict[targetFieldName];
      delete srcCellTextFormatOverrideDict[targetFieldName];
      delete srcCellFreeTextsOverrideDict[targetFieldName];
    }

    if (
      Object.keys(srcCellOverrideDict).length > 0 ||
      Object.keys(srcCellTextFormatOverrideDict).length > 0 ||
      Object.keys(srcCellFreeTextsOverrideDict).length > 0
    ) {
      this.unusedCellOverrides = {
        freeText: srcCellFreeTextsOverrideDict,
        cell: srcCellOverrideDict,
        formatting: srcCellTextFormatOverrideDict
      };
    }
  }

  ClearOverrides() {
    this.Journal();
    this.fOverrides = {};
    this.unusedCellOverrides = null;
    for (let aCellOverride in this.fCellList) {
      //TODO: Explicit cell clear method too.
      const theCellOverride = this.fCellList[aCellOverride];
      theCellOverride.fOverrides = {};
      if (theCellOverride.fTextFormat) {
        theCellOverride.fTextFormat.fOverrides = {};
        theCellOverride.fTextFormat.fFontString = null;
      }
    }
  }

  TransferGroupProps(srcLayout) {
    if (
      this.fContainerType !== enumContainerType.nonmrb &&
      srcLayout.fContainerType !== enumContainerType.nonmrb
    ) {
      this.fContainerType = srcLayout.fContainerType;
      //UGLY HARDCODE :(
      this.fBoxType = enumBoxType.nonstandard;

      var srcAssocIDs = srcLayout.fAssociatedObjIDs;
      if (srcAssocIDs != null) {
        this.ClearAssociatedDBObjs();
        var n = srcLayout.fAssociatedObjIDs.length;
        for (var i = 0; i < n; i++) this.fAssociatedObjIDs.push(srcLayout.fAssociatedObjIDs[i]);
        var srcChildIDs = srcLayout.GetAssociatedChildrenIDs();
        var m = srcChildIDs.length;
        for (var j = 0; j < m; j++) this.fAssociatedChildrenIDs.push(srcChildIDs[j]);
      }
    }
    var containerObjId = srcLayout.fContainerObjID;
    if (containerObjId != null) this.fContainerObjID = containerObjId;
  }

  TransferConnectingLineProps(srcLayout) {
    if (srcLayout !== null) {
      this.fConnectorColor = srcLayout.fConnectorColor;
      this.fConnectorDashStyle = srcLayout.fConnectorDashStyle;
      this.fConnectorWeightStyle = srcLayout.fConnectorWeightStyle;
    }
  }

  TransferLayer(srcLayout) {
    if (srcLayout != null) this.fLayer = srcLayout.fLayer;
  }

  IsAssistant() {
    return IsAssistantStyle(this.GetBranchStyle());
  }

  GetLayoutBottom() {
    var r = this.GetBoundingRect();
    return r.fBottom;
  }

  GetLayoutTop() {
    var r = this.GetBoundingRect();
    return r.fTop;
  }

  SetColumnPositions(colRight, colLeft) {
    //console.log("SetColumnPositions");
    if (this.fColumnPositions == null) this.fColumnPositions = new QPoint();
    this.fColumnPositions.fX = colRight;
    this.fColumnPositions.fY = colLeft;
  }

  GetColumnPositions() {
    return this.fColumnPositions;
  }

  // ColumnIndex
  SetColumnIndex(colIndex) {
    this.fColumnIndex = colIndex;
  }

  GetColumnIndex() {
    return this.fColumnIndex;
  }

  // RowPairMinBoxHeight
  SetRowPairMinBoxHeight(minHeight) {
    this.fRowPairMinHeight = minHeight;
  }

  GetRowPairMinBoxHeight() {
    return this.fRowPairMinHeight;
  }

  // Margin Mode
  GetMarginMode() {
    return this.GetOverridesKey("marginMode", this.fMarginMode);
  }

  SetDefaultBackgroundMargins() {
    this.fMarginMode = kManualMargins;
    if (this.fBMargins === null) {
      this.fBMargins = new QRect();
      this.fBMargins.InitRect(0, 0, 0, 0);
    }
    this.fBSpacing = new QRect();
    this.fBSpacing.InitRect(0, 0, 0, 0);
    return this.fBMargins;
  }

  SetMarginMode(marginMode) {
    this.fMarginMode = marginMode;
    if (marginMode == kManualMargins) {
      this.fBMargins = new QRect();
      this.fBMargins.InitRect(kHorzBoxSpacing, kVertBoxSpacing, kHorzBoxSpacing, kVertBoxSpacing);
      this.fMgrVSpacing1 = kVertBoxSpacing;
      this.fMgrVSpacing2 = kVertBoxSpacing;
      this.fBSpacing = new QRect();
      this.fBSpacing.InitRect(0, 0, 0, 0);
    } else {
      this.fBMargins = null;
      this.fMgrVSpacing1 = null;
      this.fMgrVSpacing2 = null;
      this.fBSpacing = null;
    }
    //	this.ClearSpacing();
  }

  SetOverrideMarginMode(marginMode) {
    this.Journal();
    this.fOverrides["marginMode"] = marginMode;

    if (marginMode == kManualMargins) {
      var newBMargins = new QRect();
      newBMargins.InitRect(kHorzBoxSpacing, kVertBoxSpacing, kHorzBoxSpacing, kVertBoxSpacing);
      this.fOverrides["BMargins"] = newBMargins;
      this.fOverrides["MgrVSpacing1"] = kVertBoxSpacing;
      this.fOverrides["MgrVSpacing2"] = kVertBoxSpacing;

      var newBSpacing = new QRect();
      newBSpacing.InitRect(0, 0, 0, 0);
      this.fOverrides["BSpacing"] = newBSpacing;
    } else {
      this.fOverrides["BMargins"] = null;
      this.fOverrides["MgrVSpacing1"] = null;
      this.fOverrides["MgrVSpacing2"] = null;
      this.fOverrides["BSpacing"] = null;
    }
    // this.ClearSpacing();
  }

  GetBMargins() {
    var mm = this.GetMarginMode();
    switch (mm) {
      case kDenseMargins:
        var mr = new QRect();
        var hs = Div2Floor(kHorzBoxSpacing);
        var vs = Div2Floor(kVertBoxSpacing);
        mr.InitRect(hs, vs, hs, vs);
        return mr;
        break;
      case kStandardMargins:
        var mr = new QRect();
        mr.InitRect(kHorzBoxSpacing, kVertBoxSpacing, kHorzBoxSpacing, kVertBoxSpacing);
        return mr;
        break;
      default:
        return this.GetOverridesKey("fBMargins", this.fBMargins);
    }
  }

  GetBoxMargins() {
    var marginMode = this.GetMarginMode();
    var mr = this.GetBMargins();

    switch (marginMode) {
      case kStandardMargins:
      case kDenseMargins:
        var bStyle = this.GetBorderStyle();
        var shadowOn = this.GetShadowOn();
        var top = mr.fTop;
        var bottom = mr.fBottom;
        if (bStyle == enumElementBorderStyle.noBorder && !shadowOn) {
          var fillMode = this.GetBoxFillMode();
          var fillColor = this.GetBoxFillColor();
          if (
            (fillMode == enumFillMode.noFill || fillColor == "#FFFFFF") &&
            this.GetContainerType() == enumContainerType.nonmrb
          ) {
            top = 0;
            bottom = 0;
          }
        }

        var sp = new QRect();
        sp.InitRect(mr.fLeft, top, mr.fRight, bottom);
        return sp;
        break;
      case kManualMargins:
        return mr;
        break;
    }
  }

  //Box Spacing
  GetBoxSpacing() {
    var fbSpacing = this.GetOverridesKey("boxSpacing", this.fBSpacing);
    if (fbSpacing == null) {
      var sp = new QRect();
      sp.InitRect(0, 0, 0, 0);
      return sp;
    }

    return fbSpacing;
  }

  SetBoxSpacing(boxSpacing) {
    if (boxSpacing != null) this.fBSpacing = boxSpacing.Clone();
    else this.fBSpacing = null;
  }

  SetOverrideBoxSpacing(boxSpacing) {
    this.Journal();
    if (boxSpacing != null) this.fOverrides["BSpacing"] = boxSpacing.Clone();
    else this.fOverrides["BSpacing"] = null;
  }

  AddToTopSpacing(adder) {
    if (adder != 0) {
      this.SetMarginMode(kManualMargins);
      this.fBMargins.fTop += adder;
    }
  }

  SetBoxMargins(left, top, right, bottom) {
    this.fBMargins.InitRect(left, top, right, bottom);
  }

  VSpacingCommon(vSpacing) {
    if (vSpacing == null) {
      var mm = this.GetMarginMode();
      switch (mm) {
        case kStandardMargins:
          return kVertBoxSpacing;
          break;
        case kDenseMargins:
          return Div2Floor(kVertBoxSpacing);
          break;
        default:
          return kVertBoxSpacing;
          break;
      }
    }

    return vSpacing;
  }

  // Mgr VSpacing
  GetMgrVSpacing1() {
    return this.VSpacingCommon(this.GetOverridesKey("MgrVSpacing1", this.fMgrVSpacing1));
  }

  GetMgrVSpacing2() {
    return this.VSpacingCommon(this.GetOverridesKey("MgrVSpacing2", this.fMgrVSpacing2));
  }

  SetMgrVSpacing1(vSpacing) {
    this.fMgrVSpacing1 = vSpacing;
  }

  SetOverrideMgrVSpacing1(spacing) {
    this.Journal();
    this.fOverrides["MgrVSpacing1"] = spacing;
  }

  SetMgrVSpacing2(vSpacing) {
    this.fMgrVSpacing2 = vSpacing;
  }

  SetOverrideMgrVSpacing2(spacing) {
    this.Journal();
    this.fOverrides["MgrVSpacing2"] = spacing;
  }

  SetBranchGroup(branchGroup) {
    this.fBranchGroup = branchGroup;
  }

  GetBranchGroup() {
    return this.GetOverridesKey("bgroup", this.fBranchGroup);
  }

  // Box Shape Type
  SetBoxShapeType(boxShape) {
    this.Journal();
    this.fBoxShapeType = boxShape;
  }

  SetOverrideBoxShapeType(boxShape) {
    this.Journal();
    this.fOverrides["boxShapeType"] = boxShape;
  }

  GetBoxShapeType() {
    return this.GetOverridesKey("boxShapeType", this.fBoxShapeType);
  }

  // Box Fill Mode
  SetBoxFillMode(fillMode) {
    this.Journal();
    this.fBoxFillMode = fillMode;
  }

  SetOverrideBoxFillMode(fillMode) {
    this.Journal();
    this.fOverrides["fillMode"] = fillMode;
  }

  GetBoxFillMode() {
    return this.GetOverridesKey("fillMode", this.fBoxFillMode);
  }

  // Box Fill Color
  SetBoxFillColor(fillColor) {
    this.Journal();
    this.fBoxFillColor = fillColor;
  }

  SetOverrideBoxFillColor(fillColor) {
    this.Journal();
    this.fOverrides["fillColor"] = fillColor;
  }

  GetBoxFillColor() {
    return this.GetOverridesKey("fillColor", this.fBoxFillColor);
  }

  // Box Color Gradient
  SetBoxFillColorGradient(fillColorGradient) {
    this.Journal();
    this.fBoxFillColorGradient = fillColorGradient;
  }

  SetOverrideBoxFillColorGradient(fillColorGradient) {
    this.Journal();
    this.fOverrides["fillColorGradient"] = fillColorGradient;
  }

  GetBoxFillColorGradient() {
    return this.GetOverridesKey("fillColorGradient", this.fBoxFillColorGradient);
  }

  // Border Thickness
  SetBorderThickness(thickness) {
    this.Journal();
    this.fBorderThickness = thickness;
  }

  SetOverrideBorderThickness(thickness) {
    this.Journal();
    this.fOverrides["borderThickness"] = thickness;
  }

  GetBorderThickness() {
    return this.GetOverridesKey("borderThickness", this.fBorderThickness);
  }

  // Border Style
  SetBorderStyle(borderStyle) {
    this.Journal();
    this.fBorderStyle = borderStyle;
  }

  SetOverrideBorderStyle(borderStyle) {
    this.Journal();
    this.fOverrides["borderStyle"] = borderStyle;
  }

  GetBorderStyle() {
    return this.GetOverridesKey("borderStyle", this.fBorderStyle);
  }

  // Border Style
  SetDashStyle(dashStyle) {
    this.Journal();
    this.fDashStyle = dashStyle;
  }

  SetOverrideDashStyle(dashStyle) {
    this.Journal();
    this.fOverrides["dashStyle"] = dashStyle;
  }

  GetDashStyle() {
    return this.GetOverridesKey("dashStyle", this.fDashStyle);
  }

  //===========================================================================
  // Annotation Flags
  SetFlagType(flagType) {
    this.Journal();
    this.fFlagType = flagType;
    if (flagType == enumFlagType.none) {
      this.fFlagColor1 = null;
      this.fFlagColor2 = null;
      this.fFlagColor3 = null;
      this.fFlagLabel = null;
      this.fTriPosition = 0;
    }
  }

  SetOverrideFlagType(flagType) {
    this.Journal();
    this.fOverrides["fFlagType"] = flagType;
  }

  GetFlagType() {
    return this.GetOverridesKey("fFlagType", this.fFlagType);
  }

  SetFlagColor1(flagColor1) {
    this.Journal();
    this.fFlagColor1 = flagColor1;
  }

  SetOverrideFlagColor1(flagColor1) {
    this.Journal();
    this.fOverrides["fFlagColor1"] = flagColor1;
  }

  GetFlagColor1() {
    return this.GetOverridesKey("fFlagColor1", this.fFlagColor1);
  }

  SetFlagColor2(flagColor2) {
    this.Journal();
    this.fFlagColor2 = flagColor2;
  }

  SetOverrideFlagColor2(flagColor2) {
    this.Journal();
    this.fOverrides["fFlagColor2"] = flagColor2;
  }

  GetFlagColor2() {
    return this.GetOverridesKey("fFlagColor2", this.fFlagColor2);
  }

  SetFlagColor3(flagColor3) {
    this.Journal();
    this.fFlagColor3 = flagColor3;
  }

  SetOverrideFlagColor3(flagColor3) {
    this.Journal();
    this.fOverrides["fFlagColor3"] = flagColor3;
  }

  GetFlagColor3() {
    return this.GetOverridesKey("fFlagColor3", this.fFlagColor3);
  }

  SetTriPosition(triPosition) {
    this.Journal();
    this.fTriPosition = triPosition;
  }

  SetOverrideTriPosition(triPosition) {
    this.Journal();
    this.fOverrides["fTriPosition"] = triPosition;
  }

  GetTriPosition() {
    return this.GetOverridesKey("fTriPosition", this.fTriPosition);
  }

  SetFlagLabel(flagLabel) {
    this.Journal();
    this.fFlagLabel = flagLabel;
  }

  SetOverrideFlagLabel(flagLabel) {
    this.Journal();
    this.fOverrides["fFlagLabel"] = flagLabel;
  }

  GetFlagLabel() {
    return this.GetOverridesKey("fFlagLabel", this.fFlagLabel);
  }

  //===========================================================================
  // This includes the border + the box
  PGetBoundingRect(
    includeSubChartArrow //
  ) {
    var r = new QRect();

    var w = this.GetBoxWidth();
    var h = this.GetBoxHeight(false);
    var borderStyle = this.GetBorderStyle();
    var borderWidth = this.GetBorderThickness();

    var borderS = CalcBorderWidth(borderStyle, borderWidth, true);
    var scArrowUp = 0;
    var scArrowDown = 0;
    if (includeSubChartArrow) {
      var scFlag = this.GetSubChartFlag();
      if (scFlag == enumSubChartFlag.drillup)
        scArrowUp = GetSCArrowHeight(); // + 1; //  gSCArrowVSpace;
      else if (scFlag == enumSubChartFlag.drilldown) scArrowDown = GetSCArrowHeight();
    }

    var sx = this.GetPositionX();
    var sy = this.GetPositionY();
    var ex = sx + w + borderS;
    sx = sx - borderS;

    var ey = sy + h + borderS + scArrowDown; // Math.max(borderS,scArrowDown);
    sy = sy - borderS - scArrowUp; // Math.max(borderS,scArrowUp);

    r.InitRect(sx, sy, ex, ey);
    return r;
  }

  GetBoundingRect(includeSubChartArrow = true) {
    return this.PGetBoundingRect(includeSubChartArrow);
  }

  // This width of the border (factors in style and thickness)
  GetBorderOutset() {
    var borderStyle = this.GetBorderStyle();
    var borderWidth = this.GetBorderThickness();

    var borderS = CalcBorderWidth(borderStyle, borderWidth, true);

    return borderS;
  }

  SCBreakTopAdder() {
    var scFlag = this.GetSubChartFlag();
    if (scFlag === enumSubChartFlag.drillup) return GetSCArrowHeight() + kSCArrowOffset;

    return 0;
  }

  SCBreakBottomAdder() {
    var scFlag = this.GetSubChartFlag();
    if (scFlag === enumSubChartFlag.drilldown) return GetSCArrowHeight() + kSCArrowOffset;

    return 0;
  }

  SetConnectingLineColor(lineColor) {
    this.Journal();
    this.fConnectingLineDesc.SetConnectingLineColor(lineColor);
  }

  GetConnectingLineColor() {
    return this.fConnectingLineDesc.GetConnectingLineColor();
  }

  SetConnectingLineThickness(lineThickness) {
    this.Journal();
    this.fConnectingLineDesc.SetConnectingLineThickness(lineThickness);
  }

  GetConnectingLineThickness() {
    return this.fConnectingLineDesc.GetConnectingLineThickness();
  }

  SetConnectingLineFillMode(fillMode) {
    this.Journal();
    this.fConnectingLineDesc.SetConnectingLineFillMode(fillMode);
  }

  GetConnectingLineFillMode() {
    return this.fConnectingLineDesc.GetConnectingLineFillMode();
  }

  SetConnectorDashStyle(connectorDashStyle) {
    this.Journal();
    this.fConnectingLineDesc.SetConnectorDashStyle(connectorDashStyle);
  }

  GetConnectorDashStyle() {
    return this.fConnectingLineDesc.GetConnectorDashStyle();
  }

  SetParentConnectorDashStyle(connectorDashStyle) {
    this.Journal();
    this.fConnectorDashStyle = connectorDashStyle;
  }

  SetParentConnectorWeightStyle(connectorWeight) {
    this.Journal();
    this.fConnectorWeightStyle = connectorWeight;
  }

  GetParentConnectorDashStyle() {
    return this.fConnectorDashStyle;
  }

  GetParentConnectorWeight() {
    return this.fConnectorWeightStyle;
  }

  SetParentConnectorColor(connectorColor) {
    this.Journal();
    this.fConnectorColor = connectorColor;
  }

  GetParentConnectorColor() {
    return this.fConnectorColor;
  }

  GetFillColorResult(cellDesc) {
    var cpResult = new QColorPickerResult();
    cpResult.fSelectedColor = cellDesc.GetCellFillColor();
    cpResult.fSelectedColor2 = cellDesc.GetCellFillColor2();
    cpResult.fFillMode = cellDesc.GetCellFillMode();
    return cpResult;
  }

  GetFontColorResult(cellDesc) {
    var cpResult = new QColorPickerResult();
    cpResult.fSelectedColor = cellDesc.GetFontColor();
    return cpResult;
  }

  SkipProperty(cellDesc, property) {
    var isPicture = cellDesc.IsPicture();
    var skipIt = false;

    if (isPicture) {
      switch (property) {
        case enumUpdateProperty.cellTextBold:
        case enumUpdateProperty.cellTextItalic:
        case enumUpdateProperty.cellTextUnderline:
        case enumUpdateProperty.fieldDisplayMode:
        case enumUpdateProperty.cellFontColor:
        case enumUpdateProperty.fontName:
        case enumUpdateProperty.fontSize:
        case enumUpdateProperty.cellWrapText:
          skipIt = true;
          break;
      }
    } else {
      switch (property) {
        case enumUpdateProperty.picMask:
        case enumUpdateProperty.picFrame:
          skipIt = true;
          break;
      }
    }

    return skipIt;
  }

  GetTextCellProperty(cellDesc, property) {
    var parameter = null;
    switch (property) {
      case enumUpdateProperty.cellTextBold:
        parameter = cellDesc.GetTextBold();
        break;
      case enumUpdateProperty.cellTextItalic:
        parameter = cellDesc.GetTextItalic();
        break;
      case enumUpdateProperty.cellTextUnderline:
        parameter = cellDesc.GetTextUnderline();
        break;
      case enumUpdateProperty.cellExpandRow:
        parameter = cellDesc.GetExpandRow();
        break;
      case enumUpdateProperty.cellRefType:
        parameter = cellDesc.GetCellRefType();
        break;
      case enumUpdateProperty.fieldDisplayMode:
        parameter = cellDesc.GetFieldDisplayMode();
        break;
      case enumUpdateProperty.cellFillColor:
        parameter = this.GetFillColorResult(cellDesc);
        break;
      case enumUpdateProperty.cellFontColor:
        parameter = this.GetFontColorResult(cellDesc);
        break;
      case enumUpdateProperty.fontName:
        parameter = cellDesc.GetFontName();
        break;
      case enumUpdateProperty.fontSize:
        parameter = cellDesc.GetFontSize();
        break;
      case enumUpdateProperty.cellTextAlign:
        parameter = cellDesc.GetAlignment();
        break;
      case enumUpdateProperty.cellWrapText:
        parameter = cellDesc.GetWrapText();
        break;
      case enumUpdateProperty.cellTextVAlign:
        parameter = cellDesc.GetVAlignment(true);
        break;
      case enumUpdateProperty.picFrame:
        parameter = cellDesc.GetPictureFrame();
        break;
      case enumUpdateProperty.picMask:
        parameter = cellDesc.GetPictureMask();
        break;
    }

    return parameter;
  }

  GetFirstFieldCell() {
    var numCells = this.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      if (cellDesc.GetField() != null) return cellDesc;
    }

    return null;
  }

  UpdateLayoutCells(parameter, property) {
    var numCells = this.GetNumCells();
    var recalcLayout = false;

    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      var cellNeedsRecalc = cellDesc.UpdateBoxCell(parameter, property);
      if (cellNeedsRecalc) recalcLayout = true;
    }

    return recalcLayout;
  }

  GetUsedFields() {
    //Get the list of all unique field names used by a box
    var fields = [];
    var numCells = this.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      var field = cellDesc.GetField(); //Note - a layout must be attached to the open document at this point.
      if (field && !fields.includes(field)) fields.push(field);
    }
    return fields;
  }

  FindFirstFieldOccuranceCell(fieldName) {
    //Find the first time a field is used inside a box.
    var numCells = this.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      var fieldDef = cellDesc.GetField();
      if (fieldDef && fieldDef.GetFieldName() === fieldName) return cellDesc;
    }
    return null;
  }

  FindFirstEmptyCell() {
    //Find the first time a field is used inside a box.
    var numCells = this.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      if (!cellDesc.IsOccupied() && !cellDesc.IsGutterCell()) return cellDesc;
    }
    return null;
  }

  UpdateForFieldList(fields, doc, baseLayout) {
    //This is a routine that will force a chart box to contain only the fields in the specified fields array, cross referencing a base
    //layout for information on where those fields first lived and their visual settings.

    //phase 1: blank out ones that are currently in the layout, but shouldn't be.
    //phase 2: box cleanup
    //phase 3: add in new ones
    //TODO: Handle multi-column.
    //TODO: Smart detection of photo in its own column?
    var OGOrder = baseLayout.GetUsedFields();

    var objDB = doc.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");

    var fieldsToHandle = JSON.parse(JSON.stringify(fields));
    var numCells = this.GetNumCells();

    //TODO: Handle Photo field?
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      var field = cellDesc.GetFieldSaveName();
      if (field) {
        if (!fieldsToHandle.includes(field))
          //This field isn't in the list of fields I'm supposed to do
          cellDesc.SetField(null);
        else
          fieldsToHandle = fieldsToHandle.filter(function (f) {
            return f != field;
          });
      }
    }

    //phase 2: cleanup
    var numCurCols = this.GetNumCols();
    var numCurRows = this.GetNumRows();
    //trim rows - no whitespace
    //Part 1: Clean out cells with no fields

    //Part 2: clean out rows with no filled cells
    for (var rowIdx = numCurRows; rowIdx > 0; rowIdx--) {
      var rowHasAnyField = false;
      for (var aColIdx = 1; aColIdx <= numCurCols; aColIdx++) {
        var targetCell = this.GetCellDesc(aColIdx, rowIdx);
        var hasField = targetCell.GetFieldSaveName();
        var freeText = cellDesc.GetFreeText();
        if (hasField || freeText) {
          rowHasAnyField = true;
          break;
        }
      }
      if (!rowHasAnyField) this.DeleteRow(rowIdx);
    }

    //phase 3 baybay
    var targetColInsert = numCurCols;
    for (var fIdx in fieldsToHandle) {
      numCurRows = this.GetNumRows();
      var targetRowInsert = numCurRows; //Default to the end.
      var f = fieldsToHandle[fIdx];
      //climb through the columns and try to add them
      var originalCell = baseLayout.FindFirstFieldOccuranceCell(f);
      var emptyCell = this.FindFirstEmptyCell();
      //Scenarios:
      //Phase 1: find a blank cell
      //var needsNewRow = false;	//TODO: I need a new row when I find my target row/col occupied
      if (originalCell != null) {
        var originalRow = originalCell.GetRowIndex();
        if (originalRow <= numCurRows) {
          targetRowInsert = originalRow - 1;
        }
        var originalCol = originalCell.GetColIndex();
        if (originalCol <= numCurCols) targetColInsert = originalCol;
      } else if (emptyCell != null) {
        //find first empty cell
        var emptyRow = emptyCell.GetRowIndex();
        if (emptyRow <= numCurRows) targetRowInsert = emptyRow;
        var emptyCol = emptyCell.GetColIndex();
        if (emptyCol <= numCurCols) targetColInsert = emptyCol;
      } else {
        //We didn't have an original nor an empty cell - add a row
        //needsNewRow = true;
      }

      //var insertAbove = OGOrder.includes(f);
      //TODO: All we need to do is check if the target row/col is occupied.
      var targetCell = this.GetCellDesc(targetColInsert, targetRowInsert); //offset 1 from gutters - the calls above skip it
      var needsNewRow = targetCell.IsOccupied() || targetCell.IsGutterCell();
      if (needsNewRow) {
        this.InsertRow(targetRowInsert, false);
        targetRowInsert += 1;
        targetColInsert = 1;
        targetCell = this.GetCellDesc(targetColInsert, targetRowInsert);
      }
      var fieldDef = boxClass.GetFieldDefByName(f);
      if (fieldDef) targetCell.SetField(fieldDef);
      else console.log("???");
      if (originalCell) {
        targetCell.CopyCellFormat(originalCell);
      } else {
        //Default cell properties based off the field def
        if (fieldDef.GetFieldType() === enumFieldType.number) {
          targetCell.SetFieldDisplayMode(enumFieldDisplayMode.LabelAndField);
        } else targetCell.SetFieldDisplayMode(enumFieldDisplayMode.FieldOnly);
      }
      //targetCol = this.findShortestColumn()
    }

    this.CalculateLayout();
  }

  RemoveFieldReferences(fieldRefList, updateRef) {
    var boxNeedRecalc = false;
    var numCells = this.GetNumCells();
    for (var i = 0; i < numCells; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      boxNeedRecalc = cellDesc.RemoveFieldReferences(fieldRefList, updateRef);
    }
    var aggregatedFieldRef = this.GetAggregatedFieldRef();
    if (aggregatedFieldRef != null) {
      var layoutChanged = updateRef;
      if (!updateRef) {
        for (var i in fieldRefList) {
          if (fieldRefList[i] == aggregatedFieldRef) {
            layoutChanged = true;
            break;
          }
        }
      }
      if (layoutChanged) {
        var newAggFieldRef = null;
        var doc = Globals._wsMgr.GetDoc();
        var objDB = doc.GetObjDB();
        var boxClass = objDB.GetClassByName("Box");
        var fieldRefID = aggregatedFieldRef.GetFieldID();
        var activeFieldDefRef = boxClass.GetFieldDefByID(fieldRefID);
        if (
          updateRef &&
          activeFieldDefRef != null &&
          activeFieldDefRef.GetCalcCode() === enumCalcCode.aggregatedField
        )
          newAggFieldRef = activeFieldDefRef;
        this.SetAggregatedFieldRef(newAggFieldRef);
        if (this.GetBoxSubType() === enumBoxSubTypes.enumeratedTable)
          this.GenerateEnumeratedTableLayout();
        else if (this.GetBoxSubType() === enumBoxSubTypes.refChart) this.GeneratePieChartLayout();
      }
    }
    if (boxNeedRecalc || layoutChanged) this.CalculateLayout();

    return boxNeedRecalc;
  }

  //=================================================================================================
  // Chart Centering Mode
  GetChartHorzCentering() {
    return this.fCenterChartHorz;
  }

  SetChartHorzCentering(centeringMode) {
    this.Journal();
    this.fCenterChartHorz = centeringMode;
  }

  GetChartVertCentering() {
    return this.fCenterChartVert;
  }

  SetChartVertCentering(centeringMode) {
    this.Journal();
    this.fCenterChartVert = centeringMode;
  }

  //=================================================================================================
  // Chart Positioning Mode
  GetHorzPositioning() {
    return this.fHorzPositioning;
  }

  SetHorzPositioning(horzPositioning) {
    this.Journal();
    this.fHorzPositioning = horzPositioning;
  }

  GetVertPositioning() {
    return this.fVertPositioning;
  }

  SetVertPositioning(vertPositioning) {
    this.Journal();
    this.fVertPositioning = vertPositioning;
  }

  GetDistRight() {
    return this.fDistRight;
  }

  GetDistBottom() {
    return this.fDistBottom;
  }

  SetDistRight(distRight) {
    this.fDistRight = distRight;
  }

  SetDistBottom(distBottom) {
    this.fDistBottom = distBottom;
  }

  //=================================================================================================
  // Box Styles
  IsBoxStyle() {
    return this.fIsBoxStyle;
  }

  SetIsBoxStyle(isBoxStyle) {
    this.fIsBoxStyle = isBoxStyle;

    // Clear the subchart flag
    this.fSubChartFlag = enumSubChartFlag.nobreak; // FEB28
    this.fSubChartID = null; // FEB28

    if (isBoxStyle) {
      this.fDBObjRef = null; // ART found dbObj ref on layout cloned to boxstyles panel.
      var numCells = this.GetNumCells();
      for (var i = 0; i < numCells; i++) {
        var cellDesc = this.GetIndexCellDesc(i);
        cellDesc.fField = null;
      }
      this.SetLayer(enumElementLayer.content);
    } else this.SetBoxStyleName(null);
  }

  GetBoxStyleID() {
    return this.fBoxStyleID;
  }

  SetBoxStyleID(boxStyleID) {
    this.fBoxStyleID = boxStyleID;
  }

  GetBoxStyleName() {
    return this.fBoxStyleName;
  }

  SetBoxStyleName(boxStyleName) {
    this.fBoxStyleName = boxStyleName;
  }

  GetSet() {
    return this.fSet;
  }

  FinishLoadBoxStyle() {
    var n = this.GetNumCells();
    for (var i = 0; i < n; i++) {
      var cellDesc = this.GetIndexCellDesc(i);
      cellDesc.SetParentLayout(this);
    }
  }

  GetWidthSizingMode() {
    if (
      this.GetContainerType() === enumContainerType.standardmrb &&
      this.fWidthSizingMode !== enumBoxWidthMode.fixedwidth
    ) {
      this.fWidthSizingMode = enumBoxWidthMode.autoself;
    }
    return this.fWidthSizingMode;
  }

  SetWidthSizingMode(sizingMode) {
    if (
      this.GetContainerType() === enumContainerType.standardmrb &&
      sizingMode !== enumBoxWidthMode.fixedwidth
    ) {
      sizingMode = enumBoxWidthMode.autoself;
    }
    this.Journal();
    this.fWidthSizingMode = sizingMode;
  }

  SetWidthMin(minX) {
    this.fWidthMin = minX;
  }

  SetWidthMax(maxX) {
    if (maxX == NaN) console.log("NAN!");

    this.fWidthMax = maxX;
  }

  GetWidthMin() {
    return this.fWidthMin;
  }

  GetWidthMax() {
    return this.fWidthMax;
  }

  GetHeightSizingMode() {
    return this.fHeightSizingMode;
  }

  SetHeightSizingMode(sizingMode) {
    this.fHeightSizingMode = sizingMode;
  }

  GetFixedHeight() {
    if (this.fFixedHeight == null || this.fFixedHeight == -1)
      this.fFixedHeight = this.CalcBoxHeight();

    return this.fFixedHeight;
  }

  SetFixedHeight(fixedHeight) {
    if (this.fFixedHeight != fixedHeight) {
      this.fFixedHeight = fixedHeight;
      this.fCachedHeight = null;
    }
  }

  GetSmartHeight() {
    if (this.fSmartHeight == null || this.fSmartHeight == -1)
      this.fSmartHeight = this.CalcBoxHeight();

    return this.fSmartHeight;
  }

  SetSmartHeight(smartHeight) {
    this.fSmartHeight = smartHeight;
  }

  GetContentAlignment() {
    return this.fContentAlign;
  }

  SetContentAlignment(contentAlign) {
    this.fContentAlign = contentAlign;
  }

  GetBoxType() {
    return this.fBoxType;
  }

  SetBoxType(boxType) {
    this.fBoxType = boxType;
  }

  SetBoxSubType(subType) {
    this.fBoxSubType = subType;
  }

  GetBoxSubType() {
    return this.fBoxSubType;
  }

  GetLayer() {
    return this.fLayer;
  }

  SetLayer(layer) {
    this.fLayer = layer;
  }

  //=================================================================================================
  GetLibID() {
    return this.fLayoutLibID;
  }

  SetLibID(libID) {
    this.fLayoutLibID = libID;
  }

  GetLibRefID() {
    return this.fLayoutLibRefID;
  }

  SetLibRefID(refID) {
    this.fLayoutLibRefID = refID;
  }

  //=================================================================================================
  TrimLayout() {
    this.fColWidths = null;
    this.fCellList = null;
  }

  IsValid() {
    if (isNaN(this.fBoxWidth)) return false;

    return true;
  }

  SetHighlightInfo(highlightName, highlightValue, highlightType) {
    if (this.fHighlightInfo == null) this.fHighlightInfo = {};
    if (!this.fHighlightInfo[highlightType]) this.fHighlightInfo[highlightType] = {};
    if (!this.fHighlightInfo[highlightType][highlightName])
      this.fHighlightInfo[highlightType][highlightName] = highlightValue;
  }

  ClearHighlightInfo(highlightType) {
    if (this.fHighlightInfo != null && JSON.stringify(this.fHighlightInfo) !== "{}") {
      var highlightInfo = this.fHighlightInfo;
      if (highlightType) delete this.fHighlightInfo[highlightType];
      else delete highlightInfo["dynamic"];
    }
  }

  UnassociateNode(dbObjID) {
    var newList = this.GetAssociatedObjIDs(true);
    var index = newList.indexOf(dbObjID);
    if (index > -1) newList.splice(index, 1);
    this.SetAssociatedObjIDs(newList);
  }

  AdjustFontSizeOfCellToFitVHeight(aCell, availableHeight) {
    var fsMin = 8;

    var fs = aCell.GetFontSize();
    if (fs >= fsMin) {
      fs--;
      aCell.SetFontSize(fs);
      var cellHeight = this.CalcCellHeight(aCell);
    }

    if (cellHeight < availableHeight || fs < fsMin) {
      return cellHeight;
    }

    this.AdjustFontSizeOfCellToFitVHeight(aCell, availableHeight);
  }
}
